import { drawBackdrop, loadArt, loadBackdrops, WRECK_BACKDROP, type Anchors, type Backdrop, type Sprite } from "./art";
import { AudioBus } from "./audio";
import { syncAds, trackGameStart, trackJourneyNext, trackJourneyOver, trackSiteDestroyed } from "./ads";
import { Controls } from "./input";
import { classify, createHopWatch, decodeLevel, gzipBody, loadLevel, prepareLevel, serializable, type LayoutBox } from "./load";
import { RoomClient, type MpPlayer } from "./net";
import { ensureHuman, ensureToken } from "./guard";
import { HEROES, coinFaceURL, fightOn, markOwned, ownedHero, readCoins, readHero, readPlayMode, writeCoins, writeHero, writePlayMode, type PlayMode } from "./challenge";
import { heroFx } from "./skins";
import { bossBarLift, type ScreenRect } from "./screenbox";
import { Journey, paintWarp } from "./journey";
import { ELITE_SPAWN_PCT, gateAffixFor, isBossFloor, portalHint, portalHost } from "./portal";
import { patchDetach, Sim, type Remote } from "./sim";
import { paintPunchIcon } from "./punchline";
import { ATTRACT_VIDEO, skyBlast, skyBreak, skyComet, skyCost, skyGlitch, skySetHidden, skySetReduced, skyStar, skyTime } from "./sky";
import { drawStick, makeHold, type Hold, type Pose } from "./stick";
import { drillHold, paintDrillIcon } from "./drill";
import { bytesToB64, TerrainLog } from "./terrain";
import { css } from "../shared/color";
import {
  BACKGROUNDS, CELL, DEMO_URL, FREE_WEAPON_COUNT, GRENADE_CD, MP_BANNED, PARTICLE_CAP, TINTS, WEAPONS, canonicalSkin, layoutFor, randomName, zoomFor,
} from "../shared/defs";
import { MAPS, MAP_ORDER } from "../shared/maps";
import type { Level } from "../shared/types";

type Mode = "title" | "mobile" | "badge" | "skins" | "mp" | "lobby" | "loading" | "warp" | "party" | "leave-q" | "pause" | "play" | "complete" | "vs-over" | "journey-over";

const SCREENS: Mode[] = ["title", "mobile", "badge", "skins", "mp", "lobby", "loading", "warp", "party", "leave-q", "pause", "complete", "vs-over", "journey-over"];
const DT = 1 / 60;

const TOUCH_FLOOR = 320;
const stage = must("stage");
const canvas = must("game") as HTMLCanvasElement;
const ctx = canvas.getContext("2d")!;
const controls = new Controls();
const audio = new AudioBus();
const net = new RoomClient();
const journey = new Journey();

let mode: Mode = "title";
let sim: Sim | null = null;
let level: Level | null = null;
let sprites: Record<string, Sprite> = {};
let anchors: Anchors | null = null;
let backdrops: Record<string, Backdrop> = { wreck: WRECK_BACKDROP };
let bgId = "wreck";
let versus = false;
let siteName = "";
let lastTarget = DEMO_URL;
let levelOrient: "portrait" | "landscape" | null = null;
/** Orientation captured when a load starts, same moment as the original, not when the world finishes. */
let stagedOrient: "portrait" | "landscape" = "landscape";
let viewScale = 1;
let job = 0;
let bootedV = -1;
let ignoreLevel = false;
let celebrated = false;
let doneAt = 0;
let playMs = 0;
let hintUntil = 0;
let nameUntil = 0;
let shownWeapon = -1;
let ghost = 0;
let tabDown = false;
let tick = 0;
let acc = 0;
{
  const savedSkin = localStorage.getItem("daw:skin");
  if (savedSkin && savedSkin !== "stickman" && canonicalSkin(savedSkin) === "stickman") {
    localStorage.setItem("daw:skin", "stickman");
  }
}
let previewSkin = readHero();
let characterBack: Mode = "title";
const pendingOps: Array<{ id: number; b: string; seq: number }> = [];
let joinOps: { ops: string; opSeq: number } | null = null;
const EMBED_ORIGIN = location.origin;
const FEATURED: Array<{
  from: string;
  to?: string;
  url: string;
  site: string;
  name: string;
  line: string;
  image: string;
  sponsored: boolean;
  maxH: number;
}> = [];
function must(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing #${id}`);
  return el;
}

function touch() {
  return window.matchMedia("(pointer: coarse)").matches && navigator.maxTouchPoints > 0;
}

/** Captured once at boot, same as the original. Fullscreen requested later must not flip this. */
const installed = (() => {
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    nav.standalone === true
  );
})();

function phoneOrient(): "portrait" | "landscape" {
  return touch() && !installed && window.innerHeight > window.innerWidth ? "portrait" : "landscape";
}

function narrowDesktop() {
  return !touch() && window.innerWidth < 1024;
}

function qualityScale() {
  return localStorage.getItem("daw:quality") === "low" ? 1 : 1.5;
}

function pixelRatio(inRoom: boolean) {
  const dpr = window.devicePixelRatio || 1;
  const cap = touch() ? (inRoom ? qualityScale() : 2) : 3;
  return Math.max(1, Math.min(cap, dpr));
}

function layoutBox(inRoom: boolean): LayoutBox {
  const scale = pixelRatio(inRoom);
  // Same stage inset as the original: In/cn are the canvas CSS size, 4px inside #stage.
  const cssW = Math.max(200, stage.clientWidth - 4);
  const cssH = Math.max(160, stage.clientHeight - 4);
  const layout = layoutFor(cssW, touch() && !inRoom, scale);
  return { layoutW: layout.layoutW, viewH: cssH, maxH: layout.maxH };
}

function playerName() {
  const input = must("mp-name") as HTMLInputElement;
  return input.value.trim().slice(0, 16) || "Player";
}

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]!));
}

function tintCss(index: number) {
  return css(TINTS[index % TINTS.length][0]);
}

function setText(id: string, text: string) {
  const el = document.getElementById(id);
  if (el) el.textContent = text;
}

function menuPix() {
  return Math.max(1, Math.round(CELL * viewScale));
}

function paintCity() {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.imageSmoothingEnabled = false;
  const bg = backdrops[bgId] ?? null;
  const now = performance.now();
  drawBackdrop(ctx, bg, canvas.width, canvas.height, (now / 1000) * 30, menuPix(), 0, now, false);
  darkenMenu();
}

function setLoad(p: number, msg?: string) {
  const clamped = Math.min(1, Math.max(0, p));
  if (msg !== undefined) setText("load-msg", msg);
  const pct = `${Math.round(clamped * 100)}%`;
  setText("load-pct", pct);
  (must("load-fill") as HTMLElement).style.width = pct;
  const warpFill = document.getElementById("warp-fill");
  if (warpFill) warpFill.style.width = pct;
}

let loadHide = 0;

/** Fade the loading card, then take it out of the tree so a stalled opacity cannot stay painted. */
function hideLoading(el: HTMLElement) {
  el.classList.add("hidden");
  el.style.transition = "";
  el.style.opacity = "";
  el.style.visibility = "";
  window.clearTimeout(loadHide);
  loadHide = window.setTimeout(() => {
    if (el.classList.contains("hidden")) el.classList.add("gone");
  }, 180);
}

function showLoading(el: HTMLElement) {
  window.clearTimeout(loadHide);
  el.classList.remove("gone");
  el.classList.add("hidden");
  el.style.transition = "";
  el.style.opacity = "";
  el.style.visibility = "";
  void el.offsetWidth;
  requestAnimationFrame(() => el.classList.remove("hidden"));
}

function resetOverlayScroll(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollTop = 0;
  const body = el.querySelector<HTMLElement>(".sfwin-body");
  if (body) body.scrollTop = 0;
}

function setMode(next: Mode) {
  mode = next;
  if (next === "warp") holdToasts();
  else if (next !== "play" && heroLive) shelveHero();
  const narrow = narrowDesktop();
  for (const id of SCREENS) {
    const el = document.getElementById(id);
    if (!el) continue;
    let on = id === next;
    if (id === "title") on = next === "title" && !narrow;
    if (id === "mobile") on = narrow && (next === "title" || next === "mobile");
    if (id === "loading") {
      if (on) showLoading(el);
      else hideLoading(el);
      continue;
    }
    el.classList.toggle("hidden", !on);
  }
  if (next === "vs-over" || next === "lobby" || next === "journey-over") {
    resetOverlayScroll(next);
    requestAnimationFrame(() => resetOverlayScroll(next));
  }
  document.getElementById("about")?.classList.add("hidden");
  const aboutBtn = document.getElementById("btn-about");
  if (aboutBtn) aboutBtn.style.display = next === "title" || next === "mobile" ? "" : "none";
  if (next === "title" && !touch() && !narrow) {
    window.setTimeout(() => {
      if (mode === "title") document.getElementById("url")?.focus();
    }, 50);
  }
  const hudOn = next === "play" || next === "pause" || next === "leave-q";
  must("hud").classList.toggle("hidden", !hudOn);
  const live = next === "play" || next === "pause" || next === "leave-q" || next === "complete" || next === "party" || next === "vs-over" || next === "journey-over";
  stage.classList.toggle("playing", live);
  stage.classList.toggle("waiting", next !== "play");
  document.body.classList.toggle("quick", versus && net.quick);
  must("ui").classList.toggle("versus", versus);
  const share = must("btn-share").querySelector("p");
  if (share) {
    if (versus && (next === "play" || next === "pause" || next === "lobby")) share.textContent = "Invite players";
    else if (next === "play" || next === "pause") share.textContent = "Share this map";
    else share.textContent = "Share";
  }
  must("btn-mp").style.display = next === "play" || next === "pause" ? "" : "none";
  syncVideo();
  orient();
  if ((next === "title" || next === "mp") && !net.code) unlockOrient();
  if (next === "lobby") refreshLobby();
  if (next === "skins") drawSkinPreview();
  else (document.getElementById("skins-clip") as HTMLVideoElement | null)?.pause();
  if (next === "loading" || next === "title" || next === "mp" || next === "lobby") paintCity();
  if (next === "play" || next === "pause") (document.activeElement as HTMLElement | null)?.blur?.();
  if (next === "title" || next === "pause") {
    renderHeroes();
    syncModeUi();
    maybeUnlockToast(sim?.fight.purse ?? readCoins());
  }
  syncAds(next);
}

function syncVideo() {
  const wrap = must("title-video");
  const video = wrap.querySelector("video");
  const on = ATTRACT_VIDEO !== "" && !touch() && window.innerWidth >= 1024 && (mode === "title" || mode === "mp");
  wrap.classList.toggle("on", on);
  if (!video) return;
  if (!on) {
    video.pause();
    return;
  }
  if (video.getAttribute("src") !== ATTRACT_VIDEO) video.src = ATTRACT_VIDEO;
  void video.play().catch(() => undefined);
}

function requiredOrient(): "portrait" | "landscape" | null {
  const pub = net.match?.pub?.orient;
  if (pub === "portrait" || pub === "landscape") return pub;
  if (net.code || installed) return "landscape";
  if (levelOrient && mode !== "title" && mode !== "mp") return levelOrient;
  return null;
}

function orient() {
  const portrait = window.innerHeight > window.innerWidth;
  document.body.classList.toggle("touch", touch());
  document.body.classList.toggle("portrait", portrait);
  document.body.classList.toggle("mobile", narrowDesktop());
  const need = requiredOrient();
  const wrong = need === "portrait" ? !portrait : need === "landscape" ? portrait : false;
  document.body.classList.toggle("turn", touch() && !!need && wrong);
  setText("rotate-msg", need === "portrait" ? "Turn your phone back upright to play" : "Turn your phone sideways to play");
  if (wrong && mode === "play" && !versus) {
    journey.cancelSuck();
    setMode("pause");
  }
}

function resize() {
  viewScale = pixelRatio(versus);
  const cssW = Math.max(200, stage.clientWidth - 4);
  const cssH = Math.max(160, stage.clientHeight - 4);
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  const bw = Math.max(1, Math.round(cssW * viewScale));
  const bh = Math.max(1, Math.round(cssH * viewScale));
  if (canvas.width !== bw || canvas.height !== bh) {
    canvas.width = bw;
    canvas.height = bh;
  }
  if (sim) sim.setView(canvas.width / sim.world.Z, canvas.height / sim.world.Z);
  orient();
  if (mode === "title") {
    const narrow = narrowDesktop();
    document.getElementById("title")?.classList.toggle("hidden", narrow);
    document.getElementById("mobile")?.classList.toggle("hidden", !narrow);
  }
  syncVideo();
}

// The HUD rewrites DOM every frame, so a fresh getBoundingClientRect per frame forces a layout.
// The canvas box only moves on resize/scroll: re-read it then, and at most twice a second.
let canvasRect: DOMRect | null = null;
let canvasRectAt = 0;
const dropCanvasRect = () => { canvasRect = null; };
window.addEventListener("resize", dropCanvasRect);
window.addEventListener("scroll", dropCanvasRect, true);
window.visualViewport?.addEventListener("resize", dropCanvasRect);

function worldFrom(clientX: number, clientY: number) {
  const t = performance.now();
  if (!canvasRect || t - canvasRectAt > 500) {
    canvasRect = canvas.getBoundingClientRect();
    canvasRectAt = t;
  }
  const rect = canvasRect;
  if (!sim || rect.width < 1 || rect.height < 1) return { x: clientX, y: clientY };
  return {
    x: sim.camX + ((clientX - rect.left) / rect.width) * sim.viewW,
    y: sim.camY + ((clientY - rect.top) / rect.height) * sim.viewH,
  };
}

const holdCache = new Map<string, Hold | null>();

function holdFor(id: string): Hold | null {
  if (id === "drill") {
    const cached = holdCache.get(id);
    if (cached !== undefined) return cached;
    const hold = drillHold();
    holdCache.set(id, hold);
    return hold;
  }
  const cached = holdCache.get(id);
  if (cached !== undefined) return cached;
  const sprite = sprites[id];
  const anchor = anchors?.weapons[id];
  const def = WEAPONS.find((weapon) => weapon.sprite === id);
  let hold: Hold | null = null;
  if (sprite && anchor?.grip && anchor.muzzle) {
    hold = makeHold(sprite, anchor.grip, anchor.muzzle, anchor.fore ?? null, def?.twoHanded ?? true, !!def?.grasp);
  }
  holdCache.set(id, hold);
  return hold;
}

function currentHold(): Hold | null {
  if (!sim) return null;
  return holdFor(sim.def.sprite);
}

function lockScreen(which: "current" | "landscape" = "current") {
  if (!touch()) return;
  const want = which === "current" && window.innerHeight > window.innerWidth ? "portrait" : "landscape";
  const orientation = screen.orientation as ScreenOrientation & { lock?: (orientation: string) => Promise<void> };
  const root = document.documentElement as HTMLElement & {
    requestFullscreen?: (opts?: { navigationUI?: "hide" | "show" | "auto" }) => Promise<void>;
  };
  if (document.fullscreenElement) {
    void orientation.lock?.(want).catch(() => undefined);
    return;
  }
  void root.requestFullscreen?.({ navigationUI: "hide" }).then(() => orientation.lock?.(want)).catch(() => undefined);
}

function unlockOrient() {
  try {
    screen.orientation?.unlock();
  } catch {
    /* not supported */
  }
}

function journeyPlaying(inRoom = versus): boolean {
  return journey.active && readPlayMode() === "journey" && !inRoom;
}

function gameMode(): "versus" | PlayMode {
  return versus ? "versus" : readPlayMode();
}

function siteKind(target: string): "demo" | "web" {
  return classify(target).kind === "demo" ? "demo" : "web";
}

function stopMatch() {
  job++;
  journey.end();
  document.body.classList.remove("journey");
  sim = null;
  level = null;
  versus = false;
  net.close();
  bootedV = -1;
  celebrated = false;
  bonusQueued = 0;
  bonusLive = 0;
  bonusKind = "";
  fullClearPending = 0;
  hideBonusEl();
  pendingOps.length = 0;
  joinOps = null;
  unlockOrient();
}

function applyIncoming(id: number, payload: string, seq: number) {
  if (!sim) {
    pendingOps.push({ id, b: payload, seq });
    return;
  }
  if (id === sim.id) return;
  if (seq && seq <= sim.seenOp) return;
  sim.applyOpsB64(payload);
  if (seq) sim.seenOp = Math.max(sim.seenOp, seq);
}

function flushOps() {
  const queued = pendingOps.splice(0, pendingOps.length);
  for (const op of queued) applyIncoming(op.id, op.b, op.seq);
}

function bootSim(next: Level, inRoom: boolean, carryHp?: number) {
  const scale = pixelRatio(inRoom);
  const cssW = Math.max(200, canvas.clientWidth || stage.clientWidth - 4);
  const z = zoomFor(next.width, next.height, cssW, scale, touch());
  const spots = next.spawns ?? [];
  const index = Math.max(0, (net.id || 1) - 1) % Math.max(1, spots.length);
  const spawn = spots.length ? { x: spots[index][0], y: spots[index][1] } : undefined;
  const particleScale = touch() && inRoom ? (localStorage.getItem("daw:quality") === "low" ? 0.25 : 0.5) : 1;
  sim = new Sim(next, z, audio, spawn, (kind, payload) => {
    if (kind === "die" && journeyPlaying()) {
      journey.markDead();
      return;
    }
    if (!versus || !sim) return;
    const body = payload as { to?: number; dmg?: number; w?: string; by?: number };
    if (kind === "hitp") net.send({ t: "e", e: { t: "hit", to: body.to, dmg: body.dmg, w: body.w, by: sim.id } });
    if (kind === "die") net.send({ t: "e", e: { t: "die", to: sim.id, by: body.by ?? -1, w: body.w ?? "" } });
    if (kind === "rkt" || kind === "gren" || kind === "mirv" || kind === "drill") net.send({ t: "e", e: { t: kind, ...(payload as object) } });
  }, inRoom, net.id || 1);
  patchDetach(sim);
  sim.parts.cap = Math.max(1000, Math.round(PARTICLE_CAP * particleScale));
  sim.id = net.id || 1;
  sim.online = inRoom;
  if (next.ops) {
    sim.applyOpsB64(next.ops);
    sim.seenOp = next.opSeq ?? 0;
  } else if (joinOps?.ops) {
    sim.applyOpsB64(joinOps.ops);
    sim.seenOp = joinOps.opSeq || 0;
  }
  if (inRoom) sim.world.log = new TerrainLog();
  flushOps();
  sim.banned.clear();
  if (net.quick) for (const id of MP_BANNED) sim.banned.add(id);
  applyHero(sim);
  sim.fight.purse = readCoins();
  sim.fight.enabled = !inRoom && fightOn();
  sim.downed = journeyPlaying(inRoom);
  sim.fight.setHazard(journeyPlaying(inRoom));
  sim.fight.setPressure(journeyPlaying(inRoom) ? journey.pressure() : 0);
  if (journeyPlaying(inRoom)) {
    sim.fight.armGate(journey.gateAffix, journey.gateBoss);
  }
  sim.worldDraw = (g) => {
    if (!sim || !journeyPlaying()) return;
    journey.drawWorld(g, performance.now(), portalClip, sim.camX, sim.camY, sim.world.Z);
  };
  if (carryHp != null && !inRoom) sim.hp = Math.max(1, Math.min(100, carryHp));
  // Solo only, including the default demo page. Sandbox and Challenge both keep the loot plan.
  if (!inRoom) sim.fight.arm(next.url || next.title);
  sim.onCoin = (value, x, y, gem) => {
    if (journeyPlaying()) journey.grant(value);
    flyCoin(x, y, gem);
    popCoinHud();
    maybeUnlockToast(sim?.fight.purse ?? readCoins());
  };
  level = next;
  versus = inRoom;
  if (!controls.hasPointer) {
    controls.aimX = sim.x + 220;
    controls.aimY = sim.y - 40;
  }
  celebrated = false;
  doneAt = 0;
  playMs = 0;
  if (bonusKind !== "clear") document.getElementById("bonus-toast")?.classList.add("hidden");
  shownWeapon = -1;
  ghost = 0;
  hintUntil = performance.now() + 9000;
  must("hud-hint").classList.remove("hidden");
  const box = layoutBox(inRoom);
  levelOrient = stagedOrient;
  document.documentElement.dataset.orient = `${levelOrient} layout=${box.layoutW} stage=${stage.clientWidth} touch=${touch()} room=${inRoom} ${window.innerWidth}x${window.innerHeight}`;
  resize();
  if (!inRoom && journey.active) journey.plant(sim, touch());
  audio.play("start");
}

async function countdown(mine: number) {
  setMode("party");
  setText("party-title", siteName);
  setText("party-n", `${net.players.length} in the match`);
  must("party-players").innerHTML = net.players.map((p) => `<div class="pl"><i class="dot" style="background:${tintCss(p.color)}"></i><p>${escapeHtml(p.name)}</p></div>`).join("");
  must("party-find").classList.add("hidden");
  for (const n of [3, 2, 1]) {
    setText("party-count", String(n));
    setText("party-status", "Get ready");
    await new Promise((resolve) => setTimeout(resolve, 700));
    if (mine !== job) return;
  }
}

interface RunOpts {
  /** Skip the bundled archive.org snapshot and load through the proxy. */
  live?: boolean;
  carryHp?: number;
  /** Reload the current Journey page without adding a level. */
  resume?: boolean;
  /** A portal hop. Failure stays on the page and opens another portal. */
  hop?: boolean;
}

async function startLevel(target: string, title: string, inRoom: boolean, opts?: RunOpts): Promise<boolean> {
  const mine = ++job;
  lastTarget = target;
  siteName = title;
  lockScreen("current");
  const box = layoutBox(inRoom);
  stagedOrient = box.layoutW < 1024 && window.innerHeight > window.innerWidth ? "portrait" : "landscape";
  const hop = !!opts?.hop;
  setMode(hop ? "warp" : "loading");
  if (hop) setText("warp-title", `Warping to ${title}…`);
  setText("load-host", title);
  const web = classify(target).kind === "web";
  let clock = hop ? createHopWatch() : undefined;
  const initialP = web ? 0.08 : 0.05;
  const initialMsg = web ? "Running the page" : "Fetching the page";
  clock?.note(initialP, initialMsg);
  setLoad(initialP, initialMsg);
  if (hop) paintWarp(ctx, performance.now(), 1);
  else paintCity();
  try {
    const next = await loadLevel(target, box, (msg, p) => {
      if (mine !== job) return;
      clock?.note(p, msg);
      setLoad(p, msg);
    }, !!opts?.live, clock);
    clock?.stop();
    clock = undefined;
    if (mine !== job) return false;
    next.fontFaces = next.fontFaces ?? [];
    next.title = next.title || title;
    if (!next.spawns?.length) {
      const info = Object.values(MAPS).find((map) => map.level === target);
      if (info) next.spawns = info.spawns;
    }
    setLoad(0.86, "Loading fonts and images");
    await prepareLevel(next);
    if (mine !== job) return false;
    const sliced = `Slicing ${next.glyphs.length.toLocaleString()} letters`;
    setLoad(0.94, sliced);
    if (!inRoom && journey.active) {
      // Portal hops open other sites, which need the person check. Run it now,
      // in the background, so a hop never waits on it.
      void ensureHuman().catch(() => undefined);
      journey.arrive(next.url || target, next.title || title, next.links ?? [], next.width, next.height, !!opts?.resume);
    }
    // Build and upload the world while the card is still opaque. Doing this
    // after .hidden keeps the card at full opacity for the whole upload.
    bootSim(next, inRoom, opts?.carryHp);
    revealWorld = true;
    draw(performance.now());
    revealWorld = false;
    await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
    if (mine !== job) return false;
    setLoad(1, sliced);
    if (inRoom && net.quick) await countdown(mine);
    if (mine !== job) return false;
    setText("pause-site", next.title);
    setText("vs-site", next.title);
    pinUrl(target, inRoom);
    setMode("play");
    // A portal hop and a journey resume stay in the same round; only a fresh load starts one.
    if (!opts?.hop && !opts?.resume) trackGameStart(gameMode(), siteKind(target));
    return true;
  } catch (err) {
    clock?.stop();
    if (mine !== job) return false;
    if (opts?.hop && sim) {
      setMode("play");
      return false;
    }
    if (!inRoom) journey.end();
    const message = err instanceof Error ? err.message : "The site could not be reached.";
    setMode(net.code ? "lobby" : "title");
    setText(net.code ? "lobby-err" : "err", message);
    return false;
  }
}

function userStart(raw: string) {
  fullClearPending = 0;
  if (bonusKind === "clear") {
    bonusKind = "";
    bonusLive = 0;
    hideBonusEl();
  }
  lockScreen("current");
  setText("err", "");
  const spec = classify(raw);
  if (readPlayMode() === "journey") journey.begin(spec.url);
  else journey.end();
  void startLevel(spec.url, spec.label, false);
}

async function travelTo(href: string) {
  const backTarget = lastTarget;
  const backName = siteName;
  const hp = Math.max(1, sim?.hp ?? 100);
  const stamp = job;
  if (sim) journey.seal(sim.destruction());
  const ok = await startLevel(href, portalHost(href), false, { live: true, carryHp: hp, hop: true });
  // A newer start (retry, new site) owns the outcome. Don't collapse that run.
  if (job !== stamp + 1) return;
  if (ok) trackJourneyNext(journey.level);
  if (ok || !journey.active || !sim) return;
  lastTarget = backTarget;
  siteName = backName;
  setText("pause-site", level?.title || backName);
  journey.collapse();
  const hint = must("hud-hint");
  hint.classList.add("hidden");
  hint.style.opacity = "0";
  if (journey.ready && journey.reveal()) audio.play("portalOpen");
  audio.play("portalShut");
  setMode("play");
}

function pinUrl(target: string, inRoom: boolean) {
  if (inRoom || net.code) return;
  if (new URLSearchParams(location.search).get("room")) return;
  const spec = classify(target);
  if (spec.kind !== "web" && spec.kind !== "demo") return;
  const next = new URL(location.href);
  next.searchParams.set("url", spec.url);
  next.searchParams.delete("from");
  const href = `${next.pathname}${next.search}`;
  if (href !== `${location.pathname}${location.search}`) history.replaceState(null, "", href);
}

function shareLink(): string {
  const params = new URLSearchParams(location.search);
  const room = versus && net.code ? net.code : params.get("room");
  if (room && (versus || params.has("room"))) return `${location.origin}/?room=${room}`;
  const listed = params.get("url");
  if (listed && listed !== "YOUR_WEBSITE_URL") return `${location.origin}/?url=${encodeURIComponent(listed)}`;
  const spec = classify(lastTarget);
  return `${location.origin}/?url=${encodeURIComponent(spec.url || DEMO_URL)}`;
}

async function upload(next: Level, url: string, title: string) {
  const body = await gzipBody(serializable(next));
  const res = await fetch(`/mp/${net.code}/level?key=${encodeURIComponent(net.key)}&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`, { method: "PUT", body });
  const data = (await res.json().catch(() => ({}))) as { error?: string; v?: number };
  if (!res.ok) throw new Error(data.error || "Could not share the page.");
  bootedV = data.v ?? bootedV;
}

async function hostStart(raw: string) {
  if (net.hostId !== net.id) return;
  ignoreLevel = true;
  const spec = classify(raw);
  try {
    const ok = await startLevel(spec.url, spec.label, true);
    if (ok && level) await upload(level, spec.url, level.title || spec.label);
  } catch (err) {
    setText("lobby-err", err instanceof Error ? err.message : "Could not start.");
  } finally {
    ignoreLevel = false;
  }
}

async function fetchOps(): Promise<{ ops: string; opSeq: number } | null> {
  if (!net.code) return null;
  const res = await fetch(`/mp/${net.code}/ops`);
  if (!res.ok) return null;
  const data = (await res.json().catch(() => null)) as { ops?: string; opSeq?: number } | null;
  if (!data?.ops) return null;
  return { ops: data.ops, opSeq: data.opSeq ?? 0 };
}

async function startFromRoom() {
  if (ignoreLevel || !net.match) return;
  const version = net.match.v;
  if (version === bootedV) return;
  bootedV = version;
  if (net.match.pub?.map && MAPS[net.match.pub.map]) {
    const info = MAPS[net.match.pub.map];
    joinOps = await fetchOps();
    try {
      await startLevel(info.level, info.name, true);
    } finally {
      joinOps = null;
    }
    return;
  }
  if (!net.match.url) return;
  const mine = job;
  const box = layoutBox(true);
  stagedOrient = box.layoutW < 1024 && window.innerHeight > window.innerWidth ? "portrait" : "landscape";
  setMode("loading");
  setText("load-host", net.match.title || net.match.url);
  setLoad(0.1, "Downloading the website");
  const res = await fetch(`/mp/${net.code}/level?v=${version}`);
  if (!res.ok) {
    setMode("lobby");
    setText("lobby-err", "The level is not ready.");
    bootedV = -1;
    return;
  }
  if (mine !== job && mode !== "loading") return;
  const next = await decodeLevel(await res.arrayBuffer());
  next.title = next.title || net.match.title || "Match";
  const sliced = `Slicing ${next.glyphs.length.toLocaleString()} letters`;
  setLoad(0.86, "Loading fonts and images");
  await prepareLevel(next);
  setLoad(1, sliced);
  bootSim(next, true);
  hideLoading(must("loading"));
  await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
  setMode("play");
  trackGameStart(gameMode(), siteKind(net.match.url));
}

async function quick(map: string) {
  setText("mp-err", "");
  lockScreen("current");
  try {
    await ensureToken();
  } catch (err) {
    setText("mp-err", err instanceof Error ? err.message : "Could not start a match.");
    return;
  }
  const res = await fetch("/mp/quick", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ map, orient: phoneOrient() }),
  });
  const data = (await res.json().catch(() => ({}))) as { room?: string; map?: string; error?: string };
  if (!res.ok || !data.room || !data.map || !MAPS[data.map]) {
    setText("mp-err", data.error || "Could not start a match.");
    return;
  }
  await net.connect(data.room, playerName());
  if (mode !== "play" && mode !== "loading" && mode !== "party") await startFromRoom();
}

async function createRoom() {
  setText("mp-err", "");
  try {
    await ensureToken();
  } catch (err) {
    setText("mp-err", err instanceof Error ? err.message : "Could not open a room.");
    return;
  }
  const res = await fetch("/mp/new", { method: "POST" });
  const data = (await res.json().catch(() => ({}))) as { room?: string; error?: string };
  if (!res.ok || !data.room) {
    setText("mp-err", data.error || "Could not open a room.");
    return;
  }
  await net.connect(data.room, playerName());
  setMode("lobby");
}

async function joinRoom(code: string) {
  const clean = code.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
  if (clean.length !== 6) {
    setText("mp-err", "Enter the 6 character room code.");
    return;
  }
  setText("mp-err", "");
  try {
    await net.connect(clean, playerName());
    if (net.match && net.match.state !== "lobby" && (net.match.url || net.match.pub)) await startFromRoom();
    else setMode("lobby");
  } catch (err) {
    setText("mp-err", err instanceof Error ? err.message : "Could not join that room.");
  }
}

function leaveRoom() {
  net.send({ t: "bye" });
  stopMatch();
  setMode("title");
}

function refreshLobby() {
  setText("lobby-code", net.code);
  setText("lobby-link", `${location.origin}/?room=${net.code}`);
  const host = net.hostId === net.id;
  must("lobby-host").hidden = !host;
  must("lobby-wait").hidden = host;
  must("lobby-alone").classList.toggle("hidden", net.players.length > 1);
  must("lobby-waiting").classList.toggle("hidden", net.players.length > 1);
  must("lobby-players").innerHTML = net.players.map((player) => {
    const me = player.id === net.id ? `<span class="tag-me">you</span>` : "";
    return `<div class="pl"><i class="dot" style="background:${tintCss(player.color)}"></i><p>${escapeHtml(player.name)}</p>${me}</div>`;
  }).join("");
}

function renderBoard(el: HTMLElement) {
  const rows = [...net.players].sort((a, b) => b.kills - a.kills || a.deaths - b.deaths);
  el.innerHTML = rows.map((player) => {
    const you = player.id === net.id ? " (you)" : "";
    return `<i class="dot" style="background:${tintCss(player.color)}"></i><p class="n">${escapeHtml(player.name)}${you}</p><p class="v">${player.kills}</p><p>${player.deaths}</p>`;
  }).join("");
  const title = document.getElementById("vs-board-title");
  if (title) title.textContent = `Score - first to ${net.match?.target || 10}`;
}

function showOver() {
  const winner = net.players.find((player) => player.id === net.match?.winner);
  setText("vs-winner", winner ? `${winner.name} wins` : "Time");
  setText("vs-over-site", siteName || net.match?.title || "");
  renderBoard(must("vs-over-rows"));
  const host = net.hostId === net.id;
  must("vs-over-host").hidden = net.quick || !host;
  must("vs-over-wait").hidden = net.quick || host;
  must("vs-over-pub").hidden = !net.quick;
  must("vs-over-room").hidden = net.quick;
  setMode("vs-over");
}

function ensureTabScore() {
  if (document.getElementById("tab-score")) return;
  const panel = document.createElement("div");
  panel.id = "tab-score";
  panel.className = "sfwin vs-board hidden";
  panel.innerHTML = `<div class="window-head"><p>Score</p></div><div class="sfwin-body"><div class="inner stats" id="tab-score-rows"></div></div>`;
  must("hud").append(panel);
}

function paintTabScore() {
  if (!sim) return;
  ensureTabScore();
  const seconds = Math.round(playMs / 1000);
  const clock = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  must("tab-score-rows").innerHTML = [
    ["Letters knocked off", sim.letters.popped.toLocaleString()],
    ["Elements broken", sim.blocksBroken.toLocaleString()],
    ["Explosions", sim.explosions.toLocaleString()],
    ["Shots fired", sim.shots.toLocaleString()],
    ["Time", clock],
    ["Destroyed", `${Math.round(sim.destruction() * 100)}%`],
  ].map(([label, value]) => `<p>${label}</p><p class="v">${value}</p>`).join("");
}

function showTab(down: boolean) {
  tabDown = down;
  document.getElementById("tab-score")?.classList.add("hidden");
  const board = document.getElementById("vs-board");
  if (!down || !versus || (mode !== "play" && mode !== "pause")) {
    board?.classList.add("hidden");
    return;
  }
  board?.classList.remove("hidden");
  renderBoard(must("vs-board-rows"));
}

function showComplete() {
  if (!sim) return;
  const seconds = Math.round(playMs / 1000);
  const clock = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  must("done-stats").innerHTML = [
    ["Letters knocked off", sim.letters.popped],
    ["Elements broken", sim.blocksBroken],
    ["Explosions", sim.explosions],
    ["Shots fired", sim.shots],
    ["Time", clock],
  ].map(([label, value]) => `<p>${label}</p><p class="v">${value}</p>`).join("");
  setText("done-site", level?.title || siteName);
  setMode("complete");
  trackSiteDestroyed(gameMode());
  audio.play("complete");
}

function shownDestruction(): number {
  if (!sim) return 0;
  if (journeyPlaying() && journey.debugFraction != null && Number.isFinite(journey.debugFraction)) {
    return Math.max(0, Math.min(1, journey.debugFraction));
  }
  return sim.destruction();
}

function hud() {
  if (!sim) return;
  const destroyed = shownDestruction();
  ghost += (destroyed - ghost) * 0.08;
  (must("hud-meter") as HTMLElement).style.width = `${Math.round(destroyed * 100)}%`;
  (must("hud-meter-ghost") as HTMLElement).style.width = `${Math.round(ghost * 100)}%`;
  setText("hud-pct", `${Math.round(destroyed * 100)}%`);
  if (!versus && (mode === "play" || mode === "pause" || mode === "complete")) {
    const paid = sim.fight.awardClear(destroyed);
    if (paid > 0) {
      if (journeyPlaying()) journey.grant(paid);
      showClearToast(paid);
    }
  }
  const playingJourney = journeyPlaying();
  document.body.classList.toggle("journey", playingJourney && (mode === "play" || mode === "pause"));
  document.body.classList.toggle("warping", mode === "warp" || (playingJourney && journey.phase === "suck"));
  const gate = must("hud-gate");
  const showGate = playingJourney && !journey.open && journey.gateStage !== "cleared" && (mode === "play" || mode === "pause");
  gate.classList.toggle("hidden", !showGate);
  if (showGate) gate.textContent = portalHint(journey.hintStage());
  const levelLabel = must("hud-level");
  levelLabel.classList.toggle("hidden", !playingJourney);
  if (playingJourney) levelLabel.textContent = `Lv ${journey.level}`;
  const hp = must("hud-hp");
  hp.classList.toggle("hidden", !playingJourney);
  if (playingJourney) {
    (must("hud-hpbar") as HTMLElement).style.width = `${Math.max(0, Math.min(100, Math.round(sim.hp)))}%`;
    hp.classList.toggle("low", sim.hp < 35);
  }
  const banner = must("level-banner");
  const showBanner = playingJourney && performance.now() < journey.bannerUntil && (mode === "play" || mode === "pause");
  banner.classList.toggle("hidden", !showBanner);
  if (showBanner) banner.textContent = journey.banner();
  const note = must("portal-toast");
  const showNote = playingJourney && !!journey.note && performance.now() < journey.noteUntil;
  note.classList.toggle("hidden", !showNote);
  if (showNote) note.textContent = journey.note;
  if (playingJourney && mode === "play" && !journey.open && journey.phase === "play" && journey.ready) {
    if (journey.reveal()) audio.play("portalOpen");
  }
  if (mode === "play" && !versus && !celebrated && !playingJourney) {
    if (destroyed >= 0.999) {
      if (!doneAt) doneAt = performance.now() + 900;
      else if (performance.now() >= doneAt) {
        celebrated = true;
        showComplete();
      }
    } else doneAt = 0;
  }
  if (sim.weapon !== shownWeapon) {
    shownWeapon = sim.weapon;
    const label = must("wname").querySelector("p");
    if (label) label.textContent = sim.def.name;
    must("wname").classList.remove("hidden");
    nameUntil = performance.now() + 1400;
  }
  if (performance.now() > nameUntil) must("wname").classList.add("hidden");
  maybePunchHint();
  const played = playMs / 1000;
  const hint = must("hud-hint");
  if (playingJourney && (journey.level >= 2 || journey.hintHeld)) {
    hint.classList.add("hidden");
    hint.style.opacity = "0";
  } else {
    hint.classList.remove("hidden");
    hint.style.opacity = played > 9 || (played > 3 && sim.shots > 6) ? "0" : "1";
  }
  const arrowEdge = playingJourney && mode === "play" && journey.phase === "play" ? journey.arrowEdge : "";
  if (arrowEdge) document.body.dataset.arrow = arrowEdge;
  else delete document.body.dataset.arrow;
  maybeMagnetHint();
  maybeDrillHint();
  maybeHexHint();
  maybeNovaHint();
  if (fullClearPending > 0 && mode === "play" && journey.phase === "play" && !warpTransition()) {
    if (bonusKind === "clear") fullClearPending = 0;
    else {
      const paid = fullClearPending;
      fullClearPending = 0;
      paintFullClear(paid);
    }
  }
  pumpToasts();
  const heroToast = document.getElementById("coin-toast");
  if (heroToast && !heroToast.classList.contains("hidden")) placeHeroToast(heroToast);
  if (tabDown && versus) renderBoard(must("vs-board-rows"));
  document.querySelectorAll<HTMLElement>("#hud-weapons .slot").forEach((slot) => {
    const index = Number(slot.dataset.slot);
    slot.classList.toggle("on", index === sim!.weapon);
    slot.classList.toggle("off", index >= 0 && sim!.banned.has(WEAPONS[index]?.id));
    const key = slot.querySelector(".key");
    if (key && index >= 0 && !key.classList.contains("nade-key")) {
      key.textContent = index >= FREE_WEAPON_COUNT ? "0" : String(index + 1);
    }
  });
  const droneSlot = WEAPONS.findIndex((weapon) => weapon.kind === "drone");
  const charge = document.querySelector<HTMLElement>(`#hud-weapons .slot[data-slot="${droneSlot}"] .charge`);
  if (charge) {
    charge.classList.toggle("on", sim.droneCd > 0);
    const bar = charge.querySelector("i") as HTMLElement | null;
    if (bar) bar.style.width = `${Math.max(0, Math.min(1, 1 - sim.droneCd / 5)) * 100}%`;
  }
  const coins = must("hud-coins").querySelector("p");
  if (coins) coins.textContent = String(sim.fight.purse);
  paintGrenadeCooldown(sim.grenadeCd);
  for (const id of ["magnet", "drill", "punchline", "meteor", "tesla"]) {
    const index = WEAPONS.findIndex((weapon) => weapon.id === id);
    const charge = document.querySelector<HTMLElement>(`#hud-weapons .slot[data-slot="${index}"] .charge`);
    if (!charge) continue;
    let frac = 1;
    if (id === "drill") frac = sim.drill.meter();
    else if (id === "magnet" || id === "punchline") frac = sim.fight.meter(id);
    else if (sim.def.id === id) {
      const cd = WEAPONS[index]?.cd || 1;
      frac = Math.max(0, Math.min(1, 1 - Math.max(0, sim.cd) / cd));
    }
    charge.classList.toggle("on", frac < 0.999);
    const bar = charge.querySelector("i") as HTMLElement | null;
    if (bar) bar.style.width = `${Math.max(0, Math.min(1, frac)) * 100}%`;
  }
  must("tbtn-alt").classList.toggle("dive", !!sim.drone);
  if (!versus) return;
  const me = net.players.find((player) => player.id === net.id);
  setText("vs-kills", `${me?.kills ?? 0} kills`);
  setText("vs-goal", `first to ${net.match?.target || 10}`);
  setText("vs-hp", String(Math.max(0, Math.round(sim.hp))));
  (must("vs-hpbar") as HTMLElement).style.width = `${Math.max(0, sim.hp)}%`;
  must("vs-hpmeter").classList.toggle("low", sim.hp < 35);
  const ranked = [...net.players].sort((a, b) => b.kills - a.kills);
  const place = Math.max(1, ranked.findIndex((player) => player.id === net.id) + 1);
  setText("vs-place", String(place));
  must("vs-feed").innerHTML = net.feed.map((row) => {
    const a = net.players.find((player) => player.id === row.k)?.name ?? "Someone";
    const b = net.players.find((player) => player.id === row.v)?.name ?? "someone";
    return `<p>${escapeHtml(a)} · ${escapeHtml(row.w)} · ${escapeHtml(b)}</p>`;
  }).join("");
  if (!must("vs-board").classList.contains("hidden")) renderBoard(must("vs-board-rows"));
  renderBoard(must("pause-board"));
}

function sampleRemotes(now: number) {
  if (!sim) return;
  const remotes: Remote[] = [];
  for (const player of net.players) {
    if (player.id === sim.id) continue;
    const snap = net.sample(player.id, now);
    if (!snap) continue;
    remotes.push({
      id: player.id, x: snap.x, y: snap.y, vx: snap.vx, vy: snap.vy, aim: snap.aim, facing: snap.facing,
      grounded: snap.grounded, jet: snap.jet, tumble: snap.tumble, weapon: snap.weapon, hp: snap.hp, alive: snap.alive,
      name: snap.name || player.name, tint: player.color, skin: snap.skin,
      drone: Number.isFinite(snap.dx) && Number.isFinite(snap.dy) ? { x: snap.dx!, y: snap.dy!, a: snap.da ?? 0, rotor: snap.drotor ?? 0 } : null,
    });
  }
  sim.remotes = remotes;
}

function sendState() {
  if (!sim || !versus) return;
  // Alone in a room (a quick match nobody joined, a host waiting for friends), nobody reads
  // our 20Hz position stream, and every message we send is a billed Durable Object request.
  // Then only flush the page-damage log, once a second, so a late joiner still gets the page
  // as it is. The stream resumes on the next tick after someone joins.
  const alone = !net.players.some((player) => player.id !== net.id);
  if (alone && tick % 60 !== 0) return;
  const batch = sim.world.log?.take();
  if (batch) net.send({ t: "o", b: bytesToB64(batch) });
  if (alone) return;
  net.send({
    t: "s",
    s: {
      x: sim.x, y: sim.y, vx: sim.vx, vy: sim.vy, aim: sim.aim, facing: sim.facing, g: sim.grounded,
      jet: sim.jetK, tb: sim.tumble, w: sim.weapon, hp: sim.hp, alive: sim.alive, skin: canonicalSkin(sim.skin), name: playerName(),
      dx: sim.drone?.x, dy: sim.drone?.y, da: sim.drone?.a, drotor: sim.drone?.rotor,
    },
  });
}

let shade: HTMLCanvasElement | null = null;
let reducedMotion = false;
let shadeKey = "";

function menuPanel(): HTMLElement | null {
  const id = mode === "title" && narrowDesktop() ? "mobile" : mode;
  const root = document.getElementById(id);
  if (!root || root.classList.contains("hidden") || root.classList.contains("gone")) return null;
  return root.querySelector<HTMLElement>(".title-card, .load-card");
}

function darkenMenu() {
  const card = menuPanel();
  const stageBox = canvas.getBoundingClientRect();
  const rect = card?.getBoundingClientRect();
  const key = rect
    ? `${canvas.width}x${canvas.height}|${Math.round(rect.left)}|${Math.round(rect.top)}|${Math.round(rect.right)}|${Math.round(rect.bottom)}|${Math.round(stageBox.left)}|${Math.round(stageBox.top)}|${Math.round(stageBox.width)}|${Math.round(stageBox.height)}`
    : `${canvas.width}x${canvas.height}|none`;
  if (shadeKey !== key) {
    shadeKey = key;
    const k = 0.125;
    const w = Math.max(2, Math.ceil(canvas.width * k));
    const h = Math.max(2, Math.ceil(canvas.height * k));
    if (!shade) shade = document.createElement("canvas");
    shade.width = w;
    shade.height = h;
    const g = shade.getContext("2d");
    if (!g) return;
    const img = g.createImageData(w, h);
    const data = img.data;
    const sx = stageBox.width > 0 ? (canvas.width / stageBox.width) * k : k;
    const sy = stageBox.height > 0 ? (canvas.height / stageBox.height) * k : k;
    let left = 0;
    let top = 0;
    let right = 0;
    let bottom = 0;
    let has = false;
    if (rect && stageBox.width > 0) {
      left = (rect.left - stageBox.left) * sx;
      top = (rect.top - stageBox.top) * sy;
      right = (rect.right - stageBox.left) * sx;
      bottom = (rect.bottom - stageBox.top) * sy;
      has = right > left && bottom > top;
    }
    const feather = 32 * sx;
    for (let y = 0; y < h; y++) {
      const row = y * w * 4;
      for (let x = 0; x < w; x++) {
        let alpha = 0;
        if (has) {
          const dx = x < left ? left - x : x > right ? x - right : 0;
          const dy = y < top ? top - y : y > bottom ? y - bottom : 0;
          const dist = dx === 0 && dy === 0 ? 0 : Math.hypot(dx, dy);
          alpha = dist >= feather ? 0 : 0.45 * (1 - dist / feather);
        }
        const i = row + x * 4;
        data[i] = 10;
        data[i + 1] = 5;
        data[i + 2] = 20;
        data[i + 3] = alpha * 255;
      }
    }
    g.putImageData(img, 0, 0);
  }
  if (!shade || !card) return;
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(shade, 0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingEnabled = false;
}

/** Original crosshair: black outline, white arms, pink centre. Sizes are device pixels. */
function drawCrosshair(x: number, y: number, gap: number, k: number) {
  const len = 3 * k;
  const inset = Math.round((3 + gap) * k);
  const bars: Array<[number, number, number, number]> = [
    [-inset - len, -k / 2, len, k],
    [inset, -k / 2, len, k],
    [-k / 2, -inset - len, k, len],
    [-k / 2, inset, k, len],
  ];
  ctx.fillStyle = "#1a1422";
  for (const [bx, by, bw, bh] of bars) ctx.fillRect(x + bx - k / 2, y + by - k / 2, bw + k, bh + k);
  ctx.fillRect(x - k, y - k, k * 2, k * 2);
  ctx.fillStyle = "#ffffff";
  for (const [bx, by, bw, bh] of bars) ctx.fillRect(x + bx, y + by, bw, bh);
  ctx.fillStyle = "#ff3d8b";
  ctx.fillRect(x - k / 2, y - k / 2, k, k);
}

function draw(now: number) {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.imageSmoothingEnabled = false;
  if (mode === "warp") {
    paintWarp(ctx, now, 1);
    return;
  }
  const bg = backdrops[bgId] ?? null;
  const live = sim && (revealWorld || mode === "play" || mode === "pause" || mode === "leave-q" || mode === "complete" || mode === "party" || mode === "vs-over" || mode === "journey-over" || (mode === "skins" && characterBack === "pause"));
  const pix = live && sim ? sim.world.pix : menuPix();
  const camX = live && sim ? sim.camX : (now / 1000) * 30;
  const alt = live && sim ? Math.max(0, (sim.world.H - (sim.camY + sim.viewH)) / Math.max(1, sim.world.H)) : 0;
  drawBackdrop(ctx, bg, canvas.width, canvas.height, camX, pix, alt, now, !!live);
  if (!live && (mode === "title" || mode === "mp" || mode === "lobby" || mode === "loading" || mode === "badge")) darkenMenu();
  if (sim && live) {
    const hudBoxes = topHudRects();
    portalClip = touch() ? playHudRects() : [];
    sim.fight.barLift = touch() ? bossBarLift(hudBottom(hudBoxes), viewScale, sim.world.Z) : 0;
    const chrome = chromeRects();
    const jet = sim.packT >= 0 ? sprites.jetpack ?? null : null;
    sim.draw(ctx, currentHold(), jet, anchors?.jetpack.pivot ?? null, sprites, holdFor);
    if (journeyPlaying()) journey.drawChrome(ctx, sim, now, viewScale, chrome);
    // Solo touch keeps the crosshair at the last point. Aiming clears on pointer-up; hasPointer does not.
    if (mode === "play" && (!touch() || controls.hasPointer)) {
      const rect = canvas.getBoundingClientRect();
      const sx = canvas.width / Math.max(1, rect.width);
      const sy = canvas.height / Math.max(1, rect.height);
      const px = controls.hasPointer ? (controls.clientX - rect.left) * sx : (sim.lookX - sim.camX) * sim.world.Z;
      const py = controls.hasPointer ? (controls.clientY - rect.top) * sy : (sim.lookY - sim.camY) * sim.world.Z;
      const spread = WEAPONS[sim.weapon]?.spread ?? 0;
      const gap = spread * 40 + Math.max(0, sim.recoil) * 0.6;
      drawCrosshair(Math.round(px), Math.round(py), gap, Math.max(1, Math.round(2 * viewScale)));
    }
    return;
  }
  const menu = mode === "title" || mode === "mp" || mode === "lobby" || mode === "loading" || mode === "badge" || mode === "skins";
  if (menu) drawMenuHero(now);
  if (mode === "loading") drawLoadStick(now);
}

function drawMenuHero(now: number) {
  if (mode === "loading" || !(touch() || narrowDesktop())) return;
  if (must("title-video").classList.contains("on")) return;
  const card = menuPanel();
  if (!card) return;
  const box = card.getBoundingClientRect();
  const stageBox = canvas.getBoundingClientRect();
  if (box.width < 8) return;
  ctx.save();
  ctx.setTransform(viewScale, 0, 0, viewScale, 0, 0);
  const sec = reducedMotion ? Math.min(now / 1000, 1.5) : now / 1000;
  const pose: Pose = {
    x: box.left - stageBox.left + 28,
    y: box.top - stageBox.top - 2,
    facing: 1,
    aim: !reducedMotion && sec % 5 > 3.6 ? -1.2 : -0.2,
    vx: 0,
    grounded: true,
    phase: 0,
    jet: 0,
    flip: -1,
    tumble: 0,
    skin: visualSkin(),
    who: "menu",
    sit: true,
    dt: DT,
    t: sec,
  };
  drawStick(ctx, pose, holdFor("smg"), null, null);
  ctx.restore();
}

function drawLoadStick(now: number) {
  const card = document.querySelector("#loading .load-card");
  if (!card) return;
  const box = card.getBoundingClientRect();
  const stageBox = canvas.getBoundingClientRect();
  const fill = parseFloat((must("load-fill") as HTMLElement).style.width) / 100;
  const along = Math.min(0.9, Math.max(0.1, Number.isFinite(fill) ? fill : 0.08));
  ctx.save();
  ctx.setTransform(viewScale, 0, 0, viewScale, 0, 0);
  const pose: Pose = {
    x: box.left - stageBox.left + box.width * along,
    y: box.top - stageBox.top,
    facing: 1, aim: 0.05, vx: 235,
    grounded: true, phase: now / 180, jet: 0, flip: -1, tumble: 0, skin: visualSkin(), who: "loader",
    dt: DT, t: now / 1000,
  };
  drawStick(ctx, pose, holdFor("pistol"), null, null);
  ctx.restore();
}

let frameTime = performance.now();
let lastSteps = 1;
let lastDraw = 0;
/** Draw the live world once under the still-opaque loading card. */
let revealWorld = false;

function frame(now: number) {
  if (sim && controls.hasPointer) {
    const aim = worldFrom(controls.clientX, controls.clientY);
    controls.aimX = aim.x;
    controls.aimY = aim.y;
  }
  const simulate = !!sim && (mode === "play" || mode === "complete" || mode === "party" || (versus && (mode === "pause" || mode === "leave-q" || mode === "vs-over")));
  if (simulate && sim) {
    const dt = Math.min(0.1, (now - frameTime) / 1000);
    acc += dt;
    let steps = 0;
    let first = true;
    // Original loop: at most FJ=4 steps of CJ=1/60, then the leftover accumulator is dropped.
    while (acc >= DT && steps < 4) {
      const pad = controls.read();
      if (mode !== "play") {
        pad.left = pad.right = pad.jump = pad.down = pad.fire = pad.alt = false;
        pad.jumpPressed = pad.downPressed = pad.firePressed = pad.altPressed = false;
        pad.slot = -1;
        pad.cycle = 0;
      } else if (!first) {
        pad.jumpPressed = pad.downPressed = pad.firePressed = pad.altPressed = false;
        pad.slot = -1;
        pad.cycle = 0;
      }
      sim.held = currentHold();
      if (journeyPlaying() && (journey.phase === "suck" || journey.phase === "travel")) {
        pad.left = pad.right = pad.jump = pad.down = pad.fire = pad.alt = false;
        pad.jumpPressed = pad.downPressed = pad.firePressed = pad.altPressed = false;
        pad.slot = -1;
        pad.cycle = 0;
      }
      sim.step(DT, pad);
      if (mode === "play" && journeyPlaying() && !document.hidden) {
        const frac = shownDestruction();
        journey.notePlay(frac, DT * 1000);
        if (journey.canSummonGate(frac) && sim.fight.trySpawnGate()) {
          journey.markGateSpawned();
          audio.play("roar");
        }
        if (journey.gateStage === "spawned" && sim.fight.gateCleared()) {
          journey.markGateCleared();
        }
      }
      if (mode === "play" && journeyPlaying()) {
        const warp = journey.tick(sim, DT);
        if (warp === "enter") {
          audio.play("portalIn");
          const paid = sim.fight.awardFullClear();
          if (paid > 0) {
            journey.grant(paid);
            queueFullClear(paid);
          }
        }
        if (warp === "hop") void travelTo(journey.pendingHref);
      }
      if (first && mode === "play") controls.consume();
      first = false;
      acc -= DT;
      steps++;
      tick++;
      if (mode === "play") playMs += DT * 1000;
      if (versus && tick % 3 === 0) sendState();
    }
    if (steps === 4) acc = 0;
    lastSteps = steps;
    sampleRemotes(now);
    if (journey.deadAt && mode === "play" && performance.now() >= journey.deadAt) showJourneyOver();
  }
  frameTime = now;
  // The sim steps at a fixed 60Hz. On a 120Hz phone every other frame has no step and would
  // redraw the exact same world, doubling the drawing cost for nothing: skip those.
  if (!(simulate && lastSteps === 0 && now - lastDraw < 40)) {
    draw(now);
    lastDraw = now;
  }
  if (sim) hud();
  if (mode === "skins") drawSkinPreview();
  requestAnimationFrame(frame);
}
const HERO_CLIPS = new Set(["iron", "digger", "clown", "hex", "nova"]);
let clipFor = "";

/** Specials play a recorded in-game clip; Stickman keeps the live canvas pose. Lazy: nothing loads until the panel shows that hero. */
function syncHeroClip() {
  const video = must("skins-clip") as HTMLVideoElement;
  const canvas = must("skins-preview");
  const lock = must("skins-lock");
  const clip = HERO_CLIPS.has(previewSkin);
  lock.classList.toggle("hidden", !clip || ownedHero(previewSkin));
  video.classList.toggle("hidden", !clip);
  canvas.classList.toggle("hidden", clip);
  if (!clip || mode !== "skins") {
    if (!video.paused) video.pause();
    return;
  }
  if (clipFor !== previewSkin) {
    clipFor = previewSkin;
    const base = `/assets/heroes/${previewSkin}`;
    video.poster = `${base}.jpg`;
    video.replaceChildren();
    for (const [ext, type] of [["mp4", "video/mp4"], ["webm", "video/webm"]]) {
      const s = document.createElement("source");
      s.src = `${base}.${ext}`;
      s.type = type;
      video.append(s);
    }
    video.load();
  }
  if (video.paused) void video.play().catch(() => undefined);
}

function drawSkinPreview() {
  syncHeroClip();
  if (HERO_CLIPS.has(previewSkin)) return;
  const preview = must("skins-preview") as HTMLCanvasElement;
  const w = 280;
  const h = 180;
  if (preview.width !== w) {
    preview.width = w;
    preview.height = h;
  }
  const g = preview.getContext("2d");
  if (!g) return;
  g.imageSmoothingEnabled = false;
  g.fillStyle = "#141414";
  g.fillRect(0, 0, w, h);
  heroFx.magnet = 0;
  heroFx.grab = 0;
  heroFx.charge = 0;
  drawStick(g, {
    x: 120, y: 156, facing: 1, aim: -0.2, vx: 30, grounded: true,
    phase: performance.now() / 400, jet: 0, flip: -1, tumble: 0, skin: previewSkin,
    dt: 1 / 60, t: performance.now() / 1000, twoHand: true,
  }, holdFor("smg"), null, null);
}

/** Largest opaque blob. X is halfway between the box centre and the mean, matching the original crop. */
function blobCenter(image: ImageData): [number, number] | null {
  const { width, height, data } = image;
  const seen = new Uint8Array(width * height);
  let best = 0;
  let center: [number, number] | null = null;
  const stack: number[] = [];
  for (let start = 0; start < width * height; start++) {
    if (seen[start] || !data[start * 4 + 3]) continue;
    let count = 0;
    let sumX = 0;
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    seen[start] = 1;
    stack.push(start);
    while (stack.length) {
      const here = stack.pop()!;
      const x = here % width;
      const y = (here - x) / width;
      count++;
      sumX += x;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      const next = [x > 0 ? here - 1 : -1, x < width - 1 ? here + 1 : -1, y > 0 ? here - width : -1, y < height - 1 ? here + width : -1];
      for (const n of next) {
        if (n >= 0 && !seen[n] && data[n * 4 + 3]) {
          seen[n] = 1;
          stack.push(n);
        }
      }
    }
    if (count > best) {
      best = count;
      center = [((minX + maxX + 1) / 2 + sumX / count + 0.5) / 2, (minY + maxY + 1) / 2];
    }
  }
  return center;
}

function paintThumb(target: HTMLCanvasElement, skin: string) {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const unit = Math.max(1, Math.round(2 * dpr)) / 2;
  const big = document.createElement("canvas");
  big.width = Math.round(88 * dpr * 2);
  big.height = Math.round(88 * dpr * 2);
  const g = big.getContext("2d", { willReadFrequently: true });
  if (!g) return;
  const feetX = big.width / 2 / unit;
  const feetY = big.height * 0.7 / unit;
  const who = `thumb-${skin}`;
  const iron = skin === "iron";
  const prop = iron || skin === "hex" || skin === "nova";
  heroFx.magnet = prop ? 1 : 0;
  heroFx.grab = 0;
  heroFx.charge = 0;
  for (let frame = 0; frame < 50; frame++) {
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, big.width, big.height);
    g.setTransform(unit, 0, 0, unit, 0, 0);
    g.imageSmoothingEnabled = false;
    drawStick(g, {
      x: feetX, y: feetY, facing: 1, aim: Math.atan2(20, 80), vx: 0, grounded: true,
      phase: 0, jet: 0, flip: -1, tumble: 0, skin, dt: 1 / 60, t: frame / 60, who,
      holdDist: prop ? 18 : 12, holdDrop: prop ? 6 : 12, twoHand: !prop, skipGlow: true,
    }, prop ? null : holdFor("smg"), null, null);
  }
  g.setTransform(1, 0, 0, 1, 0, 0);
  const center = blobCenter(g.getImageData(0, 0, big.width, big.height));
  const tile = Math.round(88 * dpr);
  target.width = tile;
  target.height = tile;
  const out = target.getContext("2d");
  if (!out || !center) return;
  out.imageSmoothingEnabled = false;
  out.drawImage(big, Math.round(center[0] - tile / 2), Math.round(center[1] - tile / 2), tile, tile, 0, 0, tile, tile);
}

const HERO_NOTE: Record<string, string> = {
  stickman: "Default fighter. Use the free weapon bar.",
  iron: "Hold to pull debris and foes into a ball, release to throw — stronger than a rocket.",
  digger: "Instant drill charge: bores a tunnel, then blows.",
  clown: "Spring punch the page — flying chunks smash what they hit.",
  hex: "Swing the staff to send an ember; a meteor falls where you aim.",
  nova: "Throw a Tesla coil that sticks and arcs lightning until it bursts.",
};

const EXCLUSIVE_IDS = new Set(["magnet", "drill", "punchline", "meteor", "tesla"]);

function buildCharacters() {
  const grid = must("skins-grid");
  grid.innerHTML = "";
  for (const hero of HEROES) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "skin-tile";
    btn.dataset.skin = hero.id;
    const thumb = document.createElement("canvas");
    paintThumb(thumb, hero.id);
    const name = document.createElement("p");
    name.textContent = hero.name;
    const sub = document.createElement("p");
    sub.className = "muted";
    btn.append(thumb, name, sub);
    btn.addEventListener("click", () => previewCharacter(hero.id));
    grid.append(btn);
  }
  previewCharacter(readHero());
}

function openCharacters(id: string, back: Mode) {
  characterBack = back === "pause" ? "pause" : "title";
  must("skins").classList.toggle("overlay", characterBack === "pause");
  previewCharacter(id);
  if (mode !== "skins") setMode("skins");
}

function previewCharacter(id: string) {
  const hero = HEROES.find((item) => item.id === id) ?? HEROES[0];
  previewSkin = hero.id;
  const coins = sim?.fight.purse ?? readCoins();
  const owned = ownedHero(hero.id);
  setText("skins-name", hero.name);
  setText("skins-coins", String(coins));
  // Skill blurb always — coin progress stays on the tile and unlock button.
  fillCoinLine(must("skins-note"), HERO_NOTE[hero.id] ?? "", false);
  const label = must("skins-confirm-label");
  const confirm = must("skins-confirm") as HTMLButtonElement;
  if (owned) label.textContent = "Select";
  else {
    label.replaceChildren(document.createTextNode("Unlock · "));
    label.append(coinPip(), document.createTextNode(String(hero.cost)));
  }
  confirm.disabled = !owned && coins < hero.cost;
  for (const tile of document.querySelectorAll<HTMLElement>("#skins-grid .skin-tile")) {
    const current = tile.dataset.skin === readHero();
    const picked = tile.dataset.skin === hero.id;
    tile.classList.toggle("on", picked);
    tile.classList.toggle("worn", current);
    tile.setAttribute("aria-pressed", picked ? "true" : "false");
    const heroCard = HEROES.find((item) => item.id === tile.dataset.skin);
    const sub = tile.querySelector(".muted");
    if (heroCard && sub) paintHeroPrice(tile, sub, heroCard, coins);
  }
  drawSkinPreview();
}

function closeCharacters() {
  previewSkin = readHero();
  const back = characterBack;
  setMode(back === "pause" ? "pause" : "title");
}

function confirmCharacter() {
  const hero = HEROES.find((item) => item.id === previewSkin);
  if (!hero || !chooseHero(hero.id)) {
    previewCharacter(previewSkin);
    return;
  }
  closeCharacters();
}

function visualSkin(): string {
  return readHero();
}

function applyHero(target: Sim | null = sim) {
  if (!target) return;
  const hero = readHero();
  target.skin = canonicalSkin(hero);
  for (const item of HEROES) if (item.weapon) target.banned.add(item.weapon);
  const weapon = HEROES.find((item) => item.id === hero)?.weapon;
  if (weapon) target.banned.delete(weapon);
  if (target.banned.has(target.def.id)) target.weapon = 0;
}

function chooseHero(id: string): boolean {
  const hero = HEROES.find((item) => item.id === id);
  if (!hero) return false;
  const buying = !ownedHero(id);
  if (buying) {
    const have = sim?.fight.purse ?? readCoins();
    if (have < hero.cost) return false;
    const left = writeCoins(have - hero.cost);
    if (sim) sim.fight.purse = left;
    markOwned(id);
  }
  writeHero(id);
  applyHero(sim);
  if (buying) showUnlockToast(hero.name, hero.id);
  return true;
}

let heroesReady = false;
let grenadeCooling = false;
let coinFlies = 0;
let toastTimer = 0;
let toastGen = 0;
let toastKind: "" | "hint" | "unlocked" = "";
let toastHero = "";
let bonusQueued = 0;
let bonusLive = 0;
let bonusKind: "" | "clear" | "break" = "";
/** Full Clear coins waiting to be readable after the warp. */
let fullClearPending = 0;

interface HeroNote {
  kind: "hint" | "unlocked";
  text: string;
  heroId: string;
  ms: number;
}

let heroQueue: HeroNote[] = [];
let heroLive: HeroNote | null = null;

function heroProgress(hero: (typeof HEROES)[number], coins: number) {
  if (hero.cost <= 0) return { line: "Free", frac: 1, state: "free" as const, hideMeter: true };
  if (ownedHero(hero.id)) {
    return { line: readHero() === hero.id ? "Selected" : "Owned", frac: 1, state: "owned" as const, hideMeter: true };
  }
  const frac = Math.max(0, Math.min(1, coins / hero.cost));
  return {
    line: `${Math.min(coins, hero.cost)} / ${hero.cost}`,
    frac,
    state: (coins >= hero.cost ? "ready" : "locked") as "ready" | "locked",
    hideMeter: false,
  };
}

function ensureHeroMeter(btn: HTMLElement): HTMLElement {
  let meter = btn.querySelector<HTMLElement>(":scope > .hero-meter");
  if (!meter) {
    meter = document.createElement("span");
    meter.className = "hero-meter";
    meter.append(document.createElement("i"));
    btn.append(meter);
  }
  return meter;
}

function coinPip(): HTMLElement {
  const mark = document.createElement("i");
  mark.className = "coin-pip";
  mark.setAttribute("aria-hidden", "true");
  return mark;
}

function fillCoinLine(el: Element, text: string, pip: boolean) {
  el.replaceChildren();
  if (pip) el.append(coinPip());
  el.append(document.createTextNode(text));
}

function paintHeroPrice(btn: HTMLElement, sub: Element, hero: (typeof HEROES)[number], coins: number) {
  const info = heroProgress(hero, coins);
  fillCoinLine(sub, info.line, !info.hideMeter);
  const meter = ensureHeroMeter(btn);
  meter.hidden = info.hideMeter;
  const fill = meter.querySelector("i");
  if (fill instanceof HTMLElement) fill.style.width = `${Math.round(info.frac * 100)}%`;
  btn.classList.toggle("locked", info.state === "locked" || info.state === "ready");
  btn.classList.toggle("ready", info.state === "ready");
  btn.classList.toggle("poor", info.state === "locked");
}

function hideBonusEl() {
  document.getElementById("bonus-toast")?.classList.add("hidden");
}

function hideHeroEl() {
  const el = document.getElementById("coin-toast");
  el?.classList.add("hidden");
  el?.classList.remove("punch-hint");
}

function cancelToastTimer() {
  toastGen++;
  window.clearTimeout(toastTimer);
  toastTimer = 0;
}

function armToast(ms: number, done: () => void) {
  const gen = ++toastGen;
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    if (gen !== toastGen) return;
    done();
  }, ms);
}

function dismissHeroLive() {
  if (!heroLive) return;
  heroLive = null;
  toastKind = "";
  toastHero = "";
  hideHeroEl();
  cancelToastTimer();
}

function hideCoinToast() {
  dismissHeroLive();
  pumpToasts();
}

/** Warp, including the suck that leads into it. Hero notes wait here with the bonus. */
function warpTransition(): boolean {
  if (mode === "warp") return true;
  return journeyPlaying() && (journey.phase === "suck" || journey.phase === "travel");
}

/** The off-screen arrow owns the edge. Hold the bonus until the portal is in view. */
function arrowHoldsBonus(): boolean {
  return journeyPlaying() && mode === "play" && journey.phase === "play" && journey.arrowEdge !== "";
}

function bonusOnStage(): boolean {
  return mode === "play" || mode === "pause" || mode === "leave-q";
}

/** Park a visible toast so the warp can replay it. Safe to call more than once. */
function holdToasts() {
  if (bonusKind === "clear") return;
  if (bonusLive > 0) {
    bonusQueued += bonusLive;
    bonusLive = 0;
    bonusKind = "";
  }
  hideBonusEl();
  if (heroLive) {
    heroQueue.unshift(heroLive);
    heroLive = null;
    toastKind = "";
    toastHero = "";
  }
  hideHeroEl();
  cancelToastTimer();
}

function maybeMagnetHint() {
  if (mode !== "play" || versus || !sim) return;
  if (readHero() !== "iron") return;
  if (warpTransition()) return;
  if (localStorage.getItem("daw:iron-hint") === "1") return;
  if (heroLive || heroQueue.length) return;
  if (performance.now() < nameUntil) return;
  localStorage.setItem("daw:iron-hint", "1");
  enqueueHero({
    kind: "unlocked",
    text: "Hold to pull, release to throw",
    heroId: "iron-hint",
    ms: 3200,
  });
}

function weaponLabelUp(): boolean {
  if (performance.now() < nameUntil) return true;
  const wname = document.getElementById("wname");
  return !!wname && !wname.classList.contains("hidden");
}

/** Park the Punchline hint while the weapon name is on screen. */
function parkPunchHint() {
  if (heroLive?.heroId !== "clown-hint") return;
  heroQueue.unshift(heroLive);
  heroLive = null;
  toastKind = "";
  toastHero = "";
  hideHeroEl();
  cancelToastTimer();
}

/** First time Clown is on, after the weapon label and any other toast. One shot. */
function maybePunchHint() {
  if (weaponLabelUp()) {
    parkPunchHint();
    return;
  }
  if (mode !== "play" || readHero() !== "clown") return;
  if (localStorage.getItem("daw:clown-hint") === "1") return;
  if (warpTransition()) return;
  if (heroLive || bonusLive > 0) return;
  localStorage.setItem("daw:clown-hint", "1");
  const narrow = document.body.classList.contains("touch") || window.innerWidth < 540;
  enqueueHero({
    kind: "unlocked",
    text: narrow
      ? "Punchline: punch the page!"
      : "Punchline: punch the page — loose chunks fly and smash what they hit",
    heroId: "clown-hint",
    ms: 4200,
  });
}

function showUnlockToast(name: string, heroId: string) {
  localStorage.setItem(`daw:told:${heroId}`, "1");
  enqueueHero({ kind: "unlocked", text: `${name} unlocked!`, heroId, ms: 1800 });
}

function drillEquipped(): boolean {
  return !!sim && sim.def.id === "drill";
}

function drillHintText(): string {
  return touch()
    ? "Drill bores in, then blows"
    : "Press 0 — it bores through, then blows";
}

/** Keep the hint at least 8px above the hotbar, the journey line and, on touch, the thumb stick. */
function placeHeroToast(el: HTMLElement) {
  const hud = document.getElementById("hud");
  const bar = document.getElementById("hud-weapons");
  if (!hud || !bar) return;
  const hudBox = hud.getBoundingClientRect();
  if (hudBox.height < 8) return;
  let limit = bar.getBoundingClientRect().top;
  const gate = document.getElementById("hud-gate");
  if (gate && !gate.classList.contains("hidden")) {
    // Desktop keeps the journey line in the top corner. Only a line near the bottom pushes the hint up.
    const top = gate.getBoundingClientRect().top;
    if (top > hudBox.top + hudBox.height / 2) limit = Math.min(limit, top);
  }
  const stick = document.getElementById("tstick");
  if (touch() && stick) {
    const box = stick.getBoundingClientRect();
    if (box.height > 0) limit = Math.min(limit, box.top);
  }
  el.style.bottom = `${Math.max(108, Math.ceil(hudBox.bottom - limit + 8))}px`;
}

function maybeDrillHint() {
  if (warpTransition()) return;
  if (versus || !heroesReady || !sim || mode !== "play" || readHero() !== "digger") return;
  if (!drillEquipped()) {
    if (heroLive?.heroId === "drill-tip") dismissHeroLive();
    heroQueue = heroQueue.filter((item) => item.heroId !== "drill-tip");
    return;
  }
  if (localStorage.getItem("daw:hint:drill") === "1") return;
  if (heroLive?.heroId === "drill-tip" || heroQueue.some((item) => item.heroId === "drill-tip")) return;
  enqueueHero({
    kind: "hint",
    text: drillHintText(),
    heroId: "drill-tip",
    ms: 4600,
  });
}


function maybeHexHint() {
  if (mode !== "play" || versus || !sim) return;
  if (readHero() !== "hex") return;
  if (warpTransition()) return;
  if (localStorage.getItem("daw:hint:meteor") === "1") return;
  if (heroLive || heroQueue.length) return;
  if (performance.now() < nameUntil) return;
  localStorage.setItem("daw:hint:meteor", "1");
  enqueueHero({
    kind: "unlocked",
    text: "Swing to call a meteor where you aim",
    heroId: "hex-hint",
    ms: 3600,
  });
}

function maybeNovaHint() {
  if (mode !== "play" || versus || !sim) return;
  if (readHero() !== "nova") return;
  if (warpTransition()) return;
  if (localStorage.getItem("daw:hint:nova") === "1") return;
  if (heroLive || heroQueue.length) return;
  if (performance.now() < nameUntil) return;
  localStorage.setItem("daw:hint:nova", "1");
  enqueueHero({
    kind: "unlocked",
    text: touch() ? "Tesla Coil: throw, then sparks" : "Press 0 — throw a coil that arcs lightning",
    heroId: "nova-hint",
    ms: 3600,
  });
}

/** Unlock hints name a priced character. Other hints, including the drill tip, stay. */
function ownedHint(id: string): boolean {
  const hero = HEROES.find((item) => item.id === id);
  return !!hero && hero.cost > 0 && ownedHero(id);
}

function maybeUnlockToast(coins: number) {
  if (versus || !heroesReady) return;
  let changed = false;
  if (heroLive?.kind === "hint" && ownedHint(heroLive.heroId)) {
    dismissHeroLive();
    changed = true;
  }
  const before = heroQueue.length;
  heroQueue = heroQueue.filter((item) => item.kind !== "hint" || !ownedHint(item.heroId));
  if (heroQueue.length !== before) changed = true;
  if (heroLive?.kind === "unlocked" || heroQueue.some((item) => item.kind === "unlocked")) {
    if (changed) pumpToasts();
    return;
  }
  for (const hero of HEROES) {
    if (hero.cost <= 0 || ownedHero(hero.id) || coins < hero.cost) continue;
    const key = `daw:told:${hero.id}`;
    if (localStorage.getItem(key) === "1") continue;
    localStorage.setItem(key, "1");
    enqueueHero({
      kind: "hint",
      text: `${hero.name} can be unlocked — open Characters`,
      heroId: hero.id,
      ms: 4600,
    });
    return;
  }
  if (changed) pumpToasts();
}

function enqueueHero(note: HeroNote) {
  if (note.kind === "unlocked") {
    heroQueue = heroQueue.filter((item) => item.heroId !== note.heroId);
    if (heroLive?.heroId === note.heroId) dismissHeroLive();
  }
  if (heroLive?.kind === note.kind && heroLive.heroId === note.heroId) return;
  if (heroQueue.some((item) => item.kind === note.kind && item.heroId === note.heroId)) return;
  if (note.kind === "hint" && (heroLive?.kind === "unlocked" || heroQueue.some((item) => item.kind === "unlocked"))) return;
  if (note.kind === "unlocked") heroQueue.unshift(note);
  else heroQueue.push(note);
  pumpToasts();
}

function showClearToast(paid: number) {
  if (warpTransition()) {
    bonusQueued += paid;
    holdToasts();
    return;
  }
  if (arrowHoldsBonus()) {
    if (bonusLive > 0) {
      bonusQueued += bonusLive;
      bonusLive = 0;
      hideBonusEl();
      cancelToastTimer();
    }
    bonusQueued += paid;
    return;
  }
  if (bonusLive > 0) {
    paintBonus(paid);
    return;
  }
  if (heroLive) {
    heroQueue.unshift(heroLive);
    heroLive = null;
    toastKind = "";
    toastHero = "";
    hideHeroEl();
    cancelToastTimer();
  }
  paintBonus(paid);
}

function paintBonus(paid: number) {
  hideHeroEl();
  const el = document.getElementById("bonus-toast");
  if (el) {
    const label = document.createElement("span");
    label.textContent = `+${paid} Destruction bonus`;
    el.replaceChildren(coinPip(), label);
    el.classList.remove("hidden");
    bonusLive = paid;
    bonusKind = "break";
    armToast(2600, () => {
      if (bonusKind !== "break") return;
      bonusKind = "";
      bonusLive = 0;
      hideBonusEl();
      pumpToasts();
    });
  }
  popCoinHud();
  maybeUnlockToast(sim?.fight.purse ?? readCoins());
}

function queueFullClear(paid: number) {
  fullClearPending = paid;
  paintFullClear(paid);
}

function paintFullClear(paid: number) {
  if (bonusLive > 0 && bonusKind !== "clear") bonusQueued += bonusLive;
  if (heroLive) {
    heroQueue.unshift(heroLive);
    heroLive = null;
    toastKind = "";
    toastHero = "";
    hideHeroEl();
  }
  const el = document.getElementById("bonus-toast");
  if (!el) return;
  const label = document.createElement("span");
  label.textContent = `Full Clear! +${paid} coins`;
  el.replaceChildren(coinPip(), label);
  el.classList.remove("hidden");
  bonusLive = paid;
  bonusKind = "clear";
  armToast(2800, () => {
    if (bonusKind !== "clear") return;
    bonusKind = "";
    bonusLive = 0;
    hideBonusEl();
    pumpToasts();
  });
  popCoinHud();
}

function paintHero(note: HeroNote) {
  hideBonusEl();
  const el = document.getElementById("coin-toast");
  if (!el) return;
  if (note.heroId === "drill-tip") localStorage.setItem("daw:hint:drill", "1");
  el.textContent = note.text;
  el.classList.toggle("punch-hint", note.heroId === "clown-hint");
  el.classList.toggle("one-line", note.heroId === "drill-tip" || note.heroId === "iron-hint" || note.heroId === "hex-hint" || note.heroId === "nova-hint");
  el.classList.remove("hidden");
  heroLive = note;
  toastKind = note.kind;
  toastHero = note.heroId;
  armToast(note.ms, () => {
    if (heroLive !== note) return;
    heroLive = null;
    toastKind = "";
    toastHero = "";
    hideHeroEl();
    pumpToasts();
  });
}

/** The controls panel is still up (first seconds of a run). Hints wait for it. */
function helpUp(): boolean {
  const help = document.getElementById("hud-hint");
  return !!help && !help.classList.contains("hidden") && help.style.opacity !== "0";
}

/**
 * Character hints only show over live play: never over Characters, pause, or any
 * other screen, and one thing at a time with the controls panel.
 */
function heroStage(): boolean {
  return mode === "play" && !helpUp();
}

/** Put a showing hint back at the head of the queue. */
function shelveHero() {
  if (!heroLive) return;
  heroQueue.unshift(heroLive);
  heroLive = null;
  toastKind = "";
  toastHero = "";
  hideHeroEl();
  cancelToastTimer();
}

/** One toast at a time. Bonuses go first. Nothing shows during a warp. */
function pumpToasts() {
  if (warpTransition()) {
    holdToasts();
    return;
  }
  if (!heroStage() && heroLive) shelveHero();
  if (arrowHoldsBonus() && bonusLive > 0) {
    bonusQueued += bonusLive;
    bonusLive = 0;
    hideBonusEl();
    cancelToastTimer();
  }
  if (bonusLive > 0) return;
  if (bonusQueued > 0 && !arrowHoldsBonus() && bonusOnStage()) {
    if (heroLive) {
      heroQueue.unshift(heroLive);
      heroLive = null;
      toastKind = "";
      toastHero = "";
      hideHeroEl();
      cancelToastTimer();
    }
    const paid = bonusQueued;
    bonusQueued = 0;
    paintBonus(paid);
    return;
  }
  if (heroLive) return;
  if (!heroStage()) return;
  if (weaponLabelUp() && heroQueue[0]?.heroId === "clown-hint") return;
  const next = heroQueue.shift();
  if (next) paintHero(next);
}

function hudBottom(boxes: readonly ScreenRect[]): number {
  let bottom = 0;
  for (const box of boxes) bottom = Math.max(bottom, box.y + box.h);
  return bottom;
}

/** Top HUD row in canvas pixels: level, destruction, health, and the coin counter. */
let portalClip: ScreenRect[] = [];

function playHudRects(): ScreenRect[] {
  const canvasRect = canvas.getBoundingClientRect();
  if (canvasRect.width < 1 || canvasRect.height < 1) return [];
  const sx = canvas.width / canvasRect.width;
  const sy = canvas.height / canvasRect.height;
  const ids = ["hud-progress", "hud-hp", "hud-coins", "hud-level", "hud-gate", "hud-weapons", "tstick", "tbtn-alt", "tbtn-pause", "hud-hint"];
  const out: ScreenRect[] = [];
  for (const id of ids) {
    const el = document.getElementById(id);
    if (!el || el.classList.contains("hidden")) continue;
    if (Number(getComputedStyle(el).opacity) === 0) continue;
    const rect = el.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) continue;
    out.push({
      x: (rect.left - canvasRect.left) * sx,
      y: (rect.top - canvasRect.top) * sy,
      w: rect.width * sx,
      h: rect.height * sy,
    });
  }
  return out;
}

function topHudRects(): ScreenRect[] {
  const hud = document.getElementById("hud");
  if (!hud || hud.classList.contains("hidden")) return [];
  const canvasRect = canvas.getBoundingClientRect();
  if (canvasRect.width < 1 || canvasRect.height < 1) return [];
  const sx = canvas.width / canvasRect.width;
  const sy = canvas.height / canvasRect.height;
  const out: ScreenRect[] = [];
  for (const id of ["hud-progress", "hud-hp", "hud-coins"]) {
    const el = document.getElementById(id);
    if (!el || el.classList.contains("hidden")) continue;
    const rect = el.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) continue;
    out.push({
      x: (rect.left - canvasRect.left) * sx,
      y: (rect.top - canvasRect.top) * sy,
      w: rect.width * sx,
      h: rect.height * sy,
    });
  }
  return out;
}

/** Screen boxes the off-screen portal arrow must not cover. */
function chromeRects(): ScreenRect[] {
  const canvasRect = canvas.getBoundingClientRect();
  if (canvasRect.width < 1 || canvasRect.height < 1) return [];
  const sx = canvas.width / canvasRect.width;
  const sy = canvas.height / canvasRect.height;
  const out: ScreenRect[] = [];
  for (const id of ["hud-progress", "hud-hp", "hud-coins", "hud-weapons", "tstick", "tbtn-alt", "tbtn-pause", "hud-gate"]) {
    const el = document.getElementById(id);
    if (!el || el.hidden || el.classList.contains("hidden")) continue;
    const style = getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden" || Number(style.opacity) === 0) continue;
    const rect = el.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) continue;
    out.push({
      x: (rect.left - canvasRect.left) * sx,
      y: (rect.top - canvasRect.top) * sy,
      w: rect.width * sx,
      h: rect.height * sy,
    });
  }
  return out;
}

function popCoinHud() {
  const el = document.getElementById("hud-coins");
  if (!el) return;
  el.classList.remove("pop");
  void el.offsetWidth;
  el.classList.add("pop");
}

function flyCoin(x: number, y: number, gem: boolean) {
  if (!sim || coinFlies > 28) return;
  const rect = canvas.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return;
  const sx = rect.left + ((x - sim.camX) / Math.max(1, sim.viewW)) * rect.width;
  const sy = rect.top + ((y - sim.camY) / Math.max(1, sim.viewH)) * rect.height;
  const hud = document.getElementById("hud-coins");
  if (!hud) return;
  const box = hud.getBoundingClientRect();
  const tx = box.left + box.width * 0.28 - sx;
  const ty = box.top + box.height * 0.5 - sy;
  const el = document.createElement("i");
  el.className = gem ? "coin-fly gem" : "coin-fly";
  el.style.left = `${sx}px`;
  el.style.top = `${sy}px`;
  document.body.append(el);
  coinFlies++;
  const anim = el.animate(
    [
      { transform: "translate(0px, 0px) scale(1)", opacity: 1 },
      { transform: `translate(${tx * 0.55}px, ${ty * 0.15 - 16}px) scale(1.08)`, opacity: 1, offset: 0.38 },
      { transform: `translate(${tx}px, ${ty}px) scale(0.4)`, opacity: 0.2 },
    ],
    { duration: 460, easing: "cubic-bezier(.2,.7,.2,1)", fill: "forwards" },
  );
  const done = () => {
    coinFlies = Math.max(0, coinFlies - 1);
    el.remove();
  };
  anim.onfinish = done;
  anim.oncancel = done;
}

function paintGrenadeCooldown(cd: number) {
  const frac = cd > 0.02 ? Math.max(0, Math.min(1, cd / GRENADE_CD)) : 0;
  for (const node of document.querySelectorAll<HTMLElement>(".nade-cd")) {
    node.classList.toggle("on", frac > 0);
    node.style.setProperty("--p", `${Math.round(frac * 1000) / 10}%`);
  }
  const cooling = frac > 0;
  if (grenadeCooling && !cooling) {
    for (const el of document.querySelectorAll(".slot.alt, #tbtn-alt")) el.classList.add("ready");
    window.setTimeout(() => {
      for (const el of document.querySelectorAll(".slot.alt, #tbtn-alt")) el.classList.remove("ready");
    }, 280);
  }
  grenadeCooling = cooling;
}

function paintRightMouse(icon: HTMLCanvasElement) {
  // Top-down mouse: rounded body, a dark split between the buttons, right button orange.
  const rows = [
    "....#####....",
    "...#LL#RR#...",
    "..#LLL#RRR#..",
    "..#LLL#RRR#..",
    "..#LLL#RRR#..",
    "...##S##S#...",
    "..#BBBBBBB#..",
    "..#BBBHBBB#..",
    "..#BBBBBBB#..",
    "..#BBBBBBB#..",
    "..#BBBBBBB#..",
    "..#BBBBBBB#..",
    "..#BBBBBBB#..",
    "..#BBBBBBB#..",
    "...#BBBBB#...",
    "...#BBBBB#...",
    "....#BBB#....",
    ".....#B#.....",
    ".....###.....",
    "......#......",
  ];
  const color: Record<string, string> = {
    "#": "#161311",
    S: "#161311",
    L: "#e2e8ee",
    R: "#ff7a2e",
    B: "#a8b2bc",
    H: "#ecf0f4",
  };
  icon.width = rows[0].length;
  icon.height = rows.length;
  const g = icon.getContext("2d");
  if (!g) return;
  g.imageSmoothingEnabled = false;
  rows.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      const ink = color[row[x]];
      if (!ink) continue;
      g.fillStyle = ink;
      g.fillRect(x, y, 1, 1);
    }
  });
}

function renderHeroes() {
  if (!heroesReady) return;
  const coins = sim?.fight.purse ?? readCoins();
  for (const id of ["title-coins", "pause-coins", "skins-coins"]) setText(id, String(coins));
  for (const root of ["title-heroes", "pause-heroes"]) {
    const box = document.getElementById(root);
    if (!box) continue;
    if (box.childElementCount !== HEROES.length) {
      box.innerHTML = "";
      for (const hero of HEROES) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "hero-pick";
        btn.dataset.hero = hero.id;
        const canvas = document.createElement("canvas");
        paintThumb(canvas, hero.id);
        const name = document.createElement("p");
        name.textContent = hero.name;
        const sub = document.createElement("p");
        sub.className = "muted";
        btn.append(canvas, name, sub);
        btn.addEventListener("click", () => openCharacters(hero.id, root === "pause-heroes" ? "pause" : "title"));
        box.append(btn);
      }
    }
    for (const btn of box.querySelectorAll<HTMLElement>(".hero-pick")) {
      const hero = HEROES.find((item) => item.id === btn.dataset.hero);
      if (!hero) continue;
      btn.classList.toggle("on", readHero() === hero.id);
      const sub = btn.querySelector(".muted");
      if (sub) paintHeroPrice(btn, sub, hero, coins);
    }
  }
}

function syncModeUi() {
  const play = readPlayMode();
  document.querySelectorAll<HTMLElement>("[data-mode]").forEach((btn) => {
    btn.classList.toggle("on", btn.dataset.mode === play);
  });
  const blurb = document.getElementById("mode-blurb");
  if (blurb) {
    blurb.textContent = play === "journey"
      ? `Fight through linked pages. At ${Math.round(ELITE_SPAWN_PCT * 100)}% an elite appears — kill it to open a portal.`
      : play === "challenge"
        ? "The page wakes up and comes after you."
        : "Sandbox. Nothing fights back.";
  }
}

function applyPlayMode(next: PlayMode) {
  writePlayMode(next);
  if (next === "journey" && sim && !versus && level && !journey.active) {
    journey.begin(level.url || lastTarget);
    journey.arrive(level.url || lastTarget, level.title || siteName, level.links ?? [], level.width, level.height, false);
    journey.plant(sim, touch());
  }
  if (sim && next !== "journey") sim.world.keep = null;
  if (sim && !versus) {
    sim.fight.enabled = next !== "sandbox";
    sim.downed = next === "journey" && journey.active;
    sim.fight.setHazard(next === "journey" && journey.active);
    sim.fight.setPressure(next === "journey" && journey.active ? journey.pressure() : 0);
    if (next === "journey" && journey.active) {
      sim.fight.armGate(journey.gateAffix, journey.gateBoss);
    }
  }
  syncModeUi();
}

function showJourneyOver() {
  if (!sim || mode === "journey-over") return;
  bonusQueued = 0;
  fullClearPending = 0;
  bonusKind = "";
  if (bonusLive > 0) {
    bonusLive = 0;
    hideBonusEl();
    cancelToastTimer();
  } else hideBonusEl();
  pumpToasts();
  journey.seal(sim.destruction());
  journey.deadAt = 0;
  setText("journey-level", `Reached Level ${journey.level}`);
  must("journey-stats").innerHTML = [
    ["Pages visited", String(journey.visits.length)],
    ["Coins earned", String(journey.earned)],
    ["Average per page", `${journey.pageAverage()}%`],
  ].map(([label, value]) => `<p>${label}</p><p class="v">${value}</p>`).join("");
  must("journey-pages").innerHTML = journey.visits.map((visit, index) => (
    `<p>${index + 1}. ${escapeHtml(visit.domain)}</p><p class="v">${Math.round(visit.destroyed * 100)}%</p>`
  )).join("");
  setMode("journey-over");
  trackJourneyOver(journey.level);
  audio.play("shatter");
}

async function copyJourney() {
  const label = document.querySelector("#journey-share p");
  try {
    await navigator.clipboard.writeText(journey.shareText(location.origin));
    if (label) label.textContent = "Copied";
  } catch {
    if (label) label.textContent = "Copy failed";
  }
  window.setTimeout(() => {
    if (label) label.textContent = "Share";
  }, 1600);
}

function paintExclusive(icon: HTMLCanvasElement, id: string) {
  icon.width = 32;
  icon.height = 16;
  icon.style.width = "64px";
  icon.style.height = "32px";
  const g = icon.getContext("2d");
  if (!g) return;
  g.imageSmoothingEnabled = false;
  const px = (x: number, y: number, color: string) => {
    g.fillStyle = color;
    g.fillRect(x, y, 1, 1);
  };
  if (id === "magnet") {
    const red = "#e23b3b";
    const shade = "#a11d1d";
    const dark = "#8e1c1c";
    const ink = "#1a1422";
    for (let y = 4; y <= 10; y++) {
      for (const x of [9, 10, 11, 20, 21, 22]) px(x, y, y < 6 ? shade : red);
      px(8, y, ink);
      px(12, y, ink);
      px(19, y, ink);
      px(23, y, ink);
    }
    px(9, 2, "#f4f7fb"); px(10, 2, "#ffffff"); px(11, 2, "#f4f7fb");
    px(20, 2, "#f4f7fb"); px(21, 2, "#ffffff"); px(22, 2, "#f4f7fb");
    px(9, 3, "#ffffff"); px(10, 3, "#f4f7fb"); px(11, 3, "#ffffff");
    px(20, 3, "#ffffff"); px(21, 3, "#f4f7fb"); px(22, 3, "#ffffff");
    for (let x = 9; x <= 22; x++) {
      px(x, 11, x < 14 ? dark : shade);
      px(x, 12, red);
      px(x, 13, dark);
    }
    px(8, 11, ink); px(23, 11, ink);
    px(7, 12, ink); px(24, 12, ink);
    px(6, 5, "#ffd0d0"); px(5, 6, "#ffffff"); px(6, 7, "#ffd0d0");
    px(25, 5, "#ffd0d0"); px(26, 6, "#ffffff"); px(25, 7, "#ffd0d0");
    px(14, 4, "#ffd0d0"); px(15, 3, "#ffffff"); px(16, 4, "#ffd0d0");
    px(17, 3, "#ffffff"); px(18, 4, "#ffd0d0");
  } else if (id === "drill") {
    paintDrillIcon(icon);
  } else if (id === "punchline") {
    paintPunchIcon(g);
  }
}

/** Hotbar icons use the trimmed frame, then the original 2× fit unless that would pass 76×44. */
function paintSlotIcon(icon: HTMLCanvasElement, sprite: Sprite) {
  const pix = sprite.pix;
  const fit = document.body.classList.contains("touch") || pix.w * 2 > 76 || pix.h * 2 > 44 ? 1 : 2;
  icon.width = pix.w;
  icon.height = pix.h;
  icon.style.width = `${pix.w * fit}px`;
  icon.style.height = `${pix.h * fit}px`;
  const g = icon.getContext("2d");
  if (!g || pix.w < 1 || pix.h < 1) return;
  const image = g.createImageData(pix.w, pix.h);
  image.data.set(new Uint8ClampedArray(pix.data.buffer, pix.data.byteOffset, pix.w * pix.h * 4));
  g.putImageData(image, 0, 0);
}

function buildHotbar() {
  const bar = must("hud-weapons");
  bar.innerHTML = "";
  WEAPONS.forEach((weapon, index) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "slot";
    btn.dataset.slot = String(index);
    btn.dataset.tap = "";
    const icon = document.createElement("canvas");
    const exclusive = EXCLUSIVE_IDS.has(weapon.id);
    const sprite = weapon.kind === "drone" ? sprites.drone ?? sprites[weapon.sprite] : sprites[weapon.sprite];
    if (weapon.id === "magnet" || weapon.id === "drill" || weapon.id === "punchline") paintExclusive(icon, weapon.id);
    else if (sprite) paintSlotIcon(icon, sprite);
    const key = document.createElement("span");
    key.className = "key";
    key.textContent = exclusive ? "0" : String(index + 1);
    btn.append(icon, key);
    if (weapon.kind === "drone" || exclusive) {
      const charge = document.createElement("div");
      charge.className = "charge";
      charge.append(document.createElement("i"));
      btn.append(charge);
    }
    btn.addEventListener("pointerdown", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      controls.slot(index);
    });
    bar.append(btn);
  });
  const grenade = document.createElement("button");
  grenade.type = "button";
  grenade.className = "slot alt";
  grenade.dataset.slot = "-1";
  grenade.dataset.tap = "";
  const icon = document.createElement("canvas");
  const sprite = sprites.grenade;
  if (sprite) {
    icon.width = sprite.w;
    icon.height = sprite.h;
    icon.style.width = `${sprite.w * 2}px`;
    icon.style.height = `${sprite.h * 2}px`;
    icon.getContext("2d")?.drawImage(sprite.img, 0, 0);
  }
  const key = document.createElement("span");
  key.className = "key nade-key";
  const mouse = document.createElement("canvas");
  mouse.className = "rmb";
  paintRightMouse(mouse);
  const gee = document.createElement("span");
  gee.className = "nade-g";
  gee.textContent = "G";
  key.append(mouse, gee);
  const cool = document.createElement("i");
  cool.className = "nade-cd";
  grenade.setAttribute("aria-label", "Grenade, right click or G");
  grenade.append(icon, key, cool);
  grenade.addEventListener("pointerdown", (ev) => {
    ev.preventDefault();
    controls.altDown();
  });
  grenade.addEventListener("pointerup", () => controls.altUp());
  grenade.addEventListener("pointercancel", () => controls.altUp());
  bar.append(grenade);
  const alt = must("tbtn-alt");
  if (sprite && !alt.querySelector("canvas")) {
    const mini = document.createElement("canvas");
    mini.width = sprite.w;
    mini.height = sprite.h;
    mini.getContext("2d")?.drawImage(sprite.img, 0, 0);
    alt.prepend(mini);
  }
}

function buildMaps() {
  const box = must("mp-maps");
  const any = document.createElement("button");
  any.type = "button";
  any.className = "map-tile random";
  any.innerHTML = `<span class="any">?</span><p>Random</p>`;
  any.addEventListener("click", () => void quick("any"));
  box.append(any);
  for (const id of MAP_ORDER) {
    const info = MAPS[id];
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "map-tile";
    btn.innerHTML = `<img src="/assets/maps/${id}/preview.jpg" alt="" loading="lazy" /><p>${escapeHtml(info.name)}</p>`;
    btn.addEventListener("click", () => void quick(id));
    box.append(btn);
  }
}

function buildBackgrounds() {
  for (const seg of document.querySelectorAll(".bg-seg")) {
    for (const bg of BACKGROUNDS) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "btn";
      btn.dataset.bg = bg.id;
      btn.innerHTML = `<span><p>${bg.name}</p></span>`;
      btn.addEventListener("click", () => {
        bgId = bg.id;
        localStorage.setItem("daw:bg", bgId);
        for (const peer of document.querySelectorAll<HTMLElement>(".bg-seg .btn")) peer.classList.toggle("on", peer.dataset.bg === bgId);
      });
      seg.append(btn);
    }
  }
  for (const peer of document.querySelectorAll<HTMLElement>(".bg-seg .btn")) peer.classList.toggle("on", peer.dataset.bg === bgId);
}

function buildBadge() {
  const look = localStorage.getItem("daw:look") === "light" ? "light" : "dark";
  applyLook(look);
  must("badge-seg").addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLElement>("[data-look]");
    if (!btn?.dataset.look) return;
    applyLook(btn.dataset.look);
  });
}

function applyLook(look: string) {
  localStorage.setItem("daw:look", look);
  must("badge-preview").classList.toggle("light", look === "light");
  for (const img of must("badge-preview").querySelectorAll("img")) img.classList.toggle("on", img.dataset.look === look);
  for (const btn of must("badge-seg").querySelectorAll(".btn")) btn.classList.toggle("on", (btn as HTMLElement).dataset.look === look);
  const file = look === "light" ? "badge-light.svg" : "badge.svg";
  const code = `<a href="${EMBED_ORIGIN}/?from=badge&url=YOUR_WEBSITE_URL" target="_blank"><img alt="Break this page" width="180" height="40" src="${EMBED_ORIGIN}/${file}"></a>`;
  setText("badge-code", code);
}

function wireInput() {
  const stick = must("tstick");
  const knob = must("tknob");
  const syncStick = () => {
    const visual = controls.visual;
    stick.classList.toggle("on", visual.on);
    if (!visual.on) {
      stick.style.left = "";
      stick.style.top = "";
      knob.style.transform = "";
      return;
    }
    stick.style.left = `${visual.x}px`;
    stick.style.top = `${visual.y}px`;
    knob.style.transform = `translate(${visual.kx}px, ${visual.ky}px)`;
  };
  window.addEventListener("pointerdown", (ev) => {
    const target = ev.target as HTMLElement | null;
    if (target?.closest?.("[data-tap]")) return;
    controls.point(ev.clientX, ev.clientY);
  });
  window.addEventListener("pointermove", (ev) => {
    if (ev.pointerType === "mouse" || ev.pointerType === "pen") controls.point(ev.clientX, ev.clientY);
  });
  // A touch on a title button synthesizes mousemove at that point. The original
  // stores that as the aim, so a grenade thrown before any canvas aim uses it.
  window.addEventListener("mousemove", (ev) => controls.point(ev.clientX, ev.clientY));
  window.addEventListener("touchstart", (ev) => {
    const target = ev.target as HTMLElement | null;
    if (target?.closest?.("[data-tap]")) return;
    const point = ev.changedTouches[0];
    if (point) controls.point(point.clientX, point.clientY);
  }, { passive: true });
  canvas.addEventListener("pointerdown", (ev) => {
    if (mode !== "play") return;
    if (ev.pointerType !== "mouse") {
      ev.preventDefault();
      canvas.setPointerCapture(ev.pointerId);
    }
    const rect = canvas.getBoundingClientRect();
    controls.pointerDown(ev, { x: ev.clientX - rect.left, y: ev.clientY - rect.top }, rect.width, rect.height);
    syncStick();
  });
  canvas.addEventListener("pointermove", (ev) => {
    const rect = canvas.getBoundingClientRect();
    controls.pointerMove(ev, { x: ev.clientX - rect.left, y: ev.clientY - rect.top });
    syncStick();
  });
  const end = (ev: PointerEvent) => {
    controls.pointerUp(ev);
    syncStick();
  };
  canvas.addEventListener("pointerup", end);
  canvas.addEventListener("pointercancel", end);
  canvas.addEventListener("contextmenu", (ev) => ev.preventDefault());
  canvas.addEventListener("wheel", (ev) => {
    if (mode !== "play") return;
    ev.preventDefault();
    controls.wheel(ev.deltaY);
  }, { passive: false });
  window.addEventListener("keydown", (ev) => {
    const field = (ev.target as HTMLElement).closest("input, textarea");
    if (field && mode !== "play" && mode !== "pause") return;
    if ((mode === "play" || mode === "pause") && field) (document.activeElement as HTMLElement | null)?.blur?.();
    const playingKeys = mode === "play" || mode === "pause" || mode === "leave-q";
    if (playingKeys && ["Space", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Tab"].includes(ev.code)) ev.preventDefault();
    if (ev.code === "Escape") {
      if (mode === "skins") {
        closeCharacters();
        return;
      }
      if (mode === "play") {
        if (!versus) journey.cancelSuck();
        setMode(versus && net.quick ? "leave-q" : "pause");
      }
      else if (mode === "pause" || mode === "leave-q") setMode("play");
      else if (mode === "complete") setMode("play");
      return;
    }
    if (ev.code === "Tab" && (mode === "play" || mode === "pause")) {
      showTab(true);
      return;
    }
    if (mode === "play" && ev.code === "KeyG" && !ev.ctrlKey && !ev.metaKey && !ev.altKey) ev.preventDefault();
    if (mode === "play") controls.key(ev, true);
  });
  window.addEventListener("keyup", (ev) => {
    if (ev.code === "Tab") showTab(false);
    if (mode === "play" || ev.code === "KeyG") controls.key(ev, false);
  });
  window.addEventListener("blur", () => {
    controls.blur();
    if (mode === "play" && !versus) {
      journey.cancelSuck();
      setMode("pause");
    }
  });
  must("tbtn-alt").addEventListener("pointerdown", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (mode !== "play") return;
    must("tbtn-alt").setPointerCapture(ev.pointerId);
    controls.altDown();
  });
  must("tbtn-alt").addEventListener("pointerup", () => controls.altUp());
  must("tbtn-pause").addEventListener("pointerdown", (ev) => {
    ev.preventDefault();
    if (mode === "play") {
      journey.cancelSuck();
      setMode("pause");
    }
  });
  must("tbtn-leave").addEventListener("pointerdown", (ev) => {
    ev.preventDefault();
    if (mode === "play") setMode("leave-q");
  });
  document.addEventListener("gesturestart", ((ev: Event) => ev.preventDefault()) as EventListener);
  stage.addEventListener("touchmove", (ev) => {
    if (mode === "play" && ev.cancelable) ev.preventDefault();
  }, { passive: false });
}

function wireUi() {
  // Start the person check while the player types a custom address, so it is
  // usually finished by the time they press Go.
  let warmed = false;
  must("url").addEventListener("input", () => {
    const value = (must("url") as HTMLInputElement).value.trim();
    if (warmed || value.length < 4 || classify(value).kind !== "web") return;
    warmed = true;
    void ensureHuman().catch(() => {
      warmed = false;
    });
  });
  void ensureToken().catch(() => undefined);
  must("urlform").addEventListener("submit", (ev) => {
    ev.preventDefault();
    userStart((must("url") as HTMLInputElement).value);
  });
  must("lobby-form").addEventListener("submit", (ev) => {
    ev.preventDefault();
    const raw = (must("lobby-url") as HTMLInputElement).value.trim() || DEMO_URL;
    void hostStart(raw);
  });
  must("vs-joinform").addEventListener("submit", (ev) => {
    ev.preventDefault();
    void joinRoom((must("vs-code") as HTMLInputElement).value);
  });
  must("mp-open").addEventListener("click", () => setMode("mp"));
  must("mp-close").addEventListener("click", () => setMode("title"));
  must("vs-create").addEventListener("click", () => void createRoom());
  must("badge-close").addEventListener("click", () => setMode("title"));
  must("skins-close").addEventListener("click", () => closeCharacters());
  must("skins-confirm").addEventListener("click", () => confirmCharacter());
  document.getElementById("about-close")?.addEventListener("click", () => document.getElementById("about")?.classList.add("hidden"));
  must("btn-share").addEventListener("click", () => {
    void navigator.clipboard?.writeText(shareLink()).catch(() => undefined);
  });
  must("btn-mp").addEventListener("click", () => {
    stopMatch();
    setMode("mp");
  });
  must("mobile-copy").addEventListener("click", () => void navigator.clipboard?.writeText(shareLink()));
  must("badge-copy").addEventListener("click", () => void navigator.clipboard?.writeText(must("badge-code").textContent || ""));
  must("lobby-copy").addEventListener("click", () => void navigator.clipboard?.writeText(`${location.origin}/?room=${net.code}`));
  must("leave-yes").addEventListener("click", () => leaveRoom());
  must("leave-no").addEventListener("click", () => setMode("play"));
  for (const input of document.querySelectorAll<HTMLInputElement>(".sfx-vol")) {
    input.value = String(audio.volume);
    input.addEventListener("input", () => {
      audio.setVolume(Number(input.value));
      for (const peer of document.querySelectorAll<HTMLInputElement>(".sfx-vol")) peer.value = input.value;
    });
  }
  const saved = localStorage.getItem("daw:name") || randomName();
  for (const id of ["mp-name", "lobby-name"]) {
    const input = must(id) as HTMLInputElement;
    input.value = saved;
    input.addEventListener("input", () => {
      localStorage.setItem("daw:name", input.value.slice(0, 16));
      for (const peer of [must("mp-name"), must("lobby-name")] as HTMLInputElement[]) if (peer !== input) peer.value = input.value;
      net.send({ t: "name", name: playerName() });
    });
  }
  document.body.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement;
    if (target.closest("button, .tag")) audio.play("click");
    const act = target.closest<HTMLElement>("[data-act]")?.dataset.act;
    const pick = target.closest<HTMLElement>("[data-url]");
    const modeBtn = target.closest<HTMLElement>("[data-mode]");
    if (modeBtn?.dataset.mode === "sandbox" || modeBtn?.dataset.mode === "challenge" || modeBtn?.dataset.mode === "journey") {
      applyPlayMode(modeBtn.dataset.mode);
    }
    if (pick?.dataset.url && !pick.classList.contains("lobby-pick")) userStart(pick.dataset.url);
    if (pick?.dataset.url && pick.classList.contains("lobby-pick")) void hostStart(pick.dataset.url);
    if (target.closest(".embed-open")) setMode("badge");
    if (act === "resume" || act === "keep") setMode("play");
    if (act === "restart") {
      if (journeyPlaying()) void startLevel(lastTarget, siteName || classify(lastTarget).label, false, { live: journey.level > 1, carryHp: sim?.hp, resume: true });
      else void startLevel(lastTarget, siteName || classify(lastTarget).label, versus);
    }
    if (act === "journey-retry") userStart(journey.startUrl || "");
    if (act === "journey-share") void copyJourney();
    if (act === "new") {
      stopMatch();
      setMode("title");
    }
    if (act === "leave") {
      if (mode === "pause" && versus && !net.quick) leaveRoom();
      else if (mode === "lobby" || mode === "party" || mode === "vs-over") leaveRoom();
      else setMode("leave-q");
    }
    if (act === "online") {
      stopMatch();
      setMode("mp");
    }
    if (act === "lobby-quick") void quick("any");
    if (act === "rematch") net.send({ t: "rematch" });
    if (act === "again" && net.match?.pub?.map) void startLevel(MAPS[net.match.pub.map].level, MAPS[net.match.pub.map].name, true);
    if (act === "vs-new") setMode("lobby");
  });
}

function bootUrl() {
  const params = new URLSearchParams(location.search);
  const room = params.get("room");
  const url = params.get("url");
  if (room) {
    setMode("mp");
    void joinRoom(room);
    return;
  }
  if (params.get("from") === "badge") {
    const target = url && url !== "YOUR_WEBSITE_URL" ? url : document.referrer;
    if (target) userStart(target);
    return;
  }
  if (url) userStart(url);
}

function featuredToday() {
  const day = new Date().toISOString().slice(0, 10);
  let pick: (typeof FEATURED)[number] | null = null;
  for (const entry of FEATURED) {
    if (entry.from <= day && (!entry.to || day <= entry.to) && (!pick || entry.from >= pick.from)) pick = entry;
  }
  return pick;
}

function wireFeatured() {
  const entry = featuredToday();
  const card = document.getElementById("featured");
  if (!card) return;
  if (!entry) {
    card.hidden = true;
    return;
  }
  setText("featured-title", `Today: destroy ${entry.site}`);
  setText("featured-line", `${entry.name} - ${entry.line}`);
  setText("featured-go-label", `Destroy ${entry.site}`);
  const img = document.getElementById("featured-img") as HTMLImageElement | null;
  if (img && entry.image) {
    img.src = entry.image;
    img.alt = entry.name;
  }
  const sponsored = document.getElementById("featured-sponsored");
  if (sponsored) sponsored.hidden = !entry.sponsored;
  must("featured-go").addEventListener("click", () => userStart(entry.url));
  card.hidden = false;
}

function hashLayer(layer: CanvasRenderingContext2D | undefined): string {
  if (!sim || !layer) return "";
  let hash = 2166136261;
  const pixels = layer.getImageData(0, 0, sim.world.page.width, sim.world.page.height).data;
  for (let i = 0; i < pixels.length; i += 4) {
    hash ^= pixels[i];
    hash = Math.imul(hash, 16777619);
    hash ^= pixels[i + 1];
    hash = Math.imul(hash, 16777619);
    hash ^= pixels[i + 2];
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16);
}

function pageHash(): string {
  if (!sim) return "";
  let hash = 2166136261;
  const mat = sim.world.mat;
  for (let i = 0; i < mat.length; i++) {
    hash ^= mat[i];
    hash = Math.imul(hash, 16777619);
  }
  for (const part of [hashLayer(sim.world.pg), hashLayer(sim.world.tx)]) {
    for (let i = 0; i < part.length; i++) {
      hash ^= part.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
  }
  return (hash >>> 0).toString(16);
}

async function boot() {
  const storedVolume = Number(localStorage.getItem("daw:volume") ?? (localStorage.getItem("daw:sound") === "off" ? "0" : "1"));
  audio.volume = Number.isFinite(storedVolume) ? storedVolume : 1;
  bgId = localStorage.getItem("daw:bg") || "wreck";
  if (!BACKGROUNDS.some((bg) => bg.id === bgId)) bgId = "wreck";
  const ua = navigator.userAgent;
  if (touch() && /iPhone|iPod/.test(ua) && !installed) document.body.classList.add("ios-safari");
  wireInput();
  wireUi();
  buildMaps();
  buildBackgrounds();
  buildBadge();
  wireFeatured();
  const debugDestroy = new URLSearchParams(location.search).get("debugDestroy");
  if (debugDestroy != null && debugDestroy.trim() !== "" && Number.isFinite(Number(debugDestroy))) {
    journey.debugFraction = Math.max(0, Math.min(1, Number(debugDestroy)));
  }
  resize();
  setMode("title");
  new ResizeObserver(() => resize()).observe(stage);
  window.addEventListener("resize", () => resize());
  document.addEventListener("visibilitychange", () => skySetHidden(document.hidden));
  skySetHidden(document.hidden);
  const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const applyMotion = () => {
    reducedMotion = motion.matches;
    skySetReduced(motion.matches);
  };
  applyMotion();
  motion.addEventListener("change", applyMotion);
  document.addEventListener("pointerdown", () => audio.unlock());
  net.onEvent = (msg) => {
    if (msg.t === "welcome") {
      if (mode === "lobby") refreshLobby();
      if (net.quick && mode !== "play" && mode !== "loading" && mode !== "party") void startFromRoom();
    }
    if (msg.t === "level") void startFromRoom();
    if (msg.t === "match" && net.match?.state === "over") showOver();
    if (msg.t === "join" || msg.t === "leave" || msg.t === "name" || msg.t === "host") {
      if (mode === "lobby") refreshLobby();
    }
    if (msg.t === "o") applyIncoming(Number(msg.id) || 0, String(msg.b || ""), Number(msg.seq) || 0);
    if (msg.t === "e" && sim) {
      const event = msg.e as { t?: string; to?: number; dmg?: number; w?: string };
      if (event?.t === "hit" && event.to === sim.id) sim.hurt(Number(event.dmg) || 0, Number(msg.id), String(event.w || ""));
      else if (event?.t && event.t !== "hit" && event.t !== "die" && Number(msg.id) !== sim.id) sim.ghostEvent(event.t, event as Record<string, unknown>);
    }
    if (msg.t === "err") setText(mode === "lobby" ? "lobby-err" : "mp-err", String(msg.m || "Room error"));
  };
  try {
    const art = await loadArt();
    sprites = art.sprites;
    anchors = art.anchors;
  } catch (err) {
    console.warn("[destroy-any-website] sprites failed", err);
  }
  buildHotbar();
  document.documentElement.style.setProperty("--coin", `url("${coinFaceURL()}")`);
  buildCharacters();
  heroesReady = true;
  renderHeroes();
  maybeUnlockToast(readCoins());
  syncModeUi();
  try {
    backdrops = await loadBackdrops(BACKGROUNDS);
  } catch (err) {
    console.warn("[destroy-any-website] backgrounds failed", err);
  }
  void audio.load();
  const pid = localStorage.getItem("daw:pid") || crypto.randomUUID().replace(/-/g, "").slice(0, 16);
  localStorage.setItem("daw:pid", pid);
  void fetch("/api/id", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ id: pid }) }).catch(() => undefined);
  const hook = window as Window & { __daw?: Record<string, unknown> };
  hook.__daw = {
    counts: () => sim ? { parts: sim.parts.list.length, letters: sim.letters.bodies.length, slabs: sim.chunks.list.length, fire: sim.fire.cells.size, dom: document.getElementsByTagName("*").length, dpr: window.devicePixelRatio, cw: canvas.width, ch: canvas.height } : null,
    ready: () => !!sim && (mode === "play" || mode === "pause"),
    loot: () => sim?.fight.lootCount ?? 0,
    loose: () => sim?.fight.looseCoins ?? 0,
    census: () => sim?.fight.census() ?? null,
    minions: () => sim?.fight.minionCount ?? 0,
    coins: () => sim?.fight.purse ?? readCoins(),
    hero: () => readHero(),
    fight: () => !!sim?.fight.enabled,
    breakLoot: () => sim?.fight.breakFirst() ?? false,
    breakBoss: () => sim?.fight.breakBoss() ?? false,
    wake: () => sim?.fight.wakeNow() ?? false,
    monsters: () => sim?.fight.monsterTally() ?? null,
    slayAll: () => sim?.fight.slayAll() ?? 0,
    portal: () => journey.debugSnapshot(),
    gate: () => sim?.fight.gateInfo() ?? null,
    spawnGate: () => {
      if (!sim || !journeyPlaying()) return false;
      const ok = sim.fight.trySpawnGate();
      if (ok) journey.markGateSpawned();
      return ok;
    },
    forceFloor: (level: number) => {
      if (!sim || !journeyPlaying()) return null;
      const n = Math.max(1, Math.floor(Number(level) || 1));
      journey.level = n;
      journey.gateBoss = isBossFloor(n);
      journey.gateAffix = gateAffixFor(n);
      journey.gateStage = "wreck";
      journey.qualified = false;
      journey.portal = null;
      journey.note = "";
      sim.fight.resetGate(journey.gateAffix, journey.gateBoss);
      return { level: journey.level, boss: journey.gateBoss, affix: journey.gateAffix, title: journey.banner() };
    },
    hurtGate: (dmg: number) => sim?.fight.hurtGate(Number(dmg) || 0) ?? false,
    awardFullClear: () => sim?.fight.awardFullClear() ?? 0,
    monstersCleared: () => sim?.fight.monstersCleared() ?? false,
    setDestruction: (n: number | null) => {
      if (n == null || !Number.isFinite(Number(n))) journey.debugFraction = null;
      else journey.debugFraction = Math.max(0, Math.min(1, Number(n)));
    },
    peace: (n: number) => sim?.fight.peace(n),
    probe: () => {
      const fight = sim?.fight.probe() ?? null;
      if (!fight || !sim) return fight;
      return { ...fight, drill: sim.drill.snapshot() };
    },
    place: (x: number, y: number) => {
      if (!sim) return;
      sim.x = x;
      sim.y = y;
      sim.vx = 0;
      sim.vy = 0;
    },
    script: (pad: Sim["script"]) => {
      if (sim) sim.script = pad;
    },
    pick: (id: string) => chooseHero(id),
    weapon: () => sim?.def.id ?? "",
    tumble: () => sim?.tumbleV ?? 0,
    hudCoins: () => document.querySelector("#hud-coins p")?.textContent ?? "",
    mode: () => mode,
    show: (name: string) => {
      setMode(name as Mode);
      return mode;
    },
    finish: () => {
      showComplete();
      return mode;
    },
    destruction: () => sim?.destruction() ?? 0,
    glyphs: () => sim?.letters.x.length ?? 0,
    solids: () => sim?.world.totalCells ?? 0,
    hash: () => pageHash(),
    pageHash: () => hashLayer(sim?.world.pg),
    textHash: () => hashLayer(sim?.world.tx),
    grid: () => {
      if (!sim) return "";
      let hash = 2166136261;
      const mat = sim.world.mat;
      const col = sim.world.col;
      for (let i = 0; i < mat.length; i++) {
        hash ^= mat[i];
        hash = Math.imul(hash, 16777619);
        hash ^= col[i];
        hash = Math.imul(hash, 16777619);
      }
      return (hash >>> 0).toString(16);
    },
    page: () => (sim ? `${sim.world.page.width}x${sim.world.page.height}` : ""),
    textInk: () => {
      if (!sim) return 0;
      const data = sim.world.tx.getImageData(0, 0, sim.world.text.width, sim.world.text.height).data;
      let n = 0;
      for (let i = 3; i < data.length; i += 16) if (data[i]) n++;
      return n;
    },
    ground: () => {
      if (!sim) return null;
      const w = sim.world;
      const rows: Array<[number, number, number]> = [];
      for (let x = 80; x < Math.min(w.W, 1100); x += 140) {
        const a = w.findSurface(x - 8, x + 8, 0, 900);
        const b = w.findSurface(x - 8, x + 8, 30, 520);
        rows.push([Math.round(x), Number.isNaN(a) ? -1 : Math.round(a), Number.isNaN(b) ? -1 : Math.round(b)]);
      }
      return { rows, y: Math.round(sim.y), x: Math.round(sim.x) };
    },
    stats: () => {
      if (!sim) return null;
      const load = document.getElementById("load-msg");
      return {
        shots: sim.shots,
        coils: sim.coils.map((c) => ({ x: Math.round(c.x), y: Math.round(c.y), phase: c.phase, left: +c.left.toFixed(2) })),
        bolts: sim.bolts.length,
        meteors: sim.meteors.map((m) => ({ phase: m.phase, x: Math.round(m.x), y: Math.round(m.y), t: +m.t.toFixed(2), age: +m.age.toFixed(3), tx: Math.round(m.tx), ty: Math.round(m.ty), ry: Math.round(m.ry) })),
        cam: { x: Math.round(sim.camX), y: Math.round(sim.camY), w: Math.round(sim.viewW), h: Math.round(sim.viewH) },
        beams: sim.beams.map((b) => +b.life.toFixed(3)),
        rings: sim.rings.length,
        drone: sim.drone ? { x: Math.round(sim.drone.x), y: Math.round(sim.drone.y), st: sim.drone.state, py: Math.round(sim.y) } : null,
        mirvs: sim.mirvs.map((m) => ({ st: m.state, y: Math.round(m.y) })),
        explosions: sim.explosions,
        blocksBroken: sim.blocksBroken,
        letters: sim.letters.popped,
        flying: sim.letters.bodies.length,
        slabs: sim.chunks.list.length,
        hint: getComputedStyle(document.getElementById("hud-hint")!).opacity,
        score: !document.getElementById("tab-score")?.classList.contains("hidden"),
        promo: !!document.querySelector(".pack-promo:not([hidden])"),
        madeOn: !!document.querySelector(".made-on:not([hidden])"),
        load: load?.textContent ?? "",
        pct: document.getElementById("load-pct")?.textContent ?? "",
        x: Math.round(sim.x),
        y: Math.round(sim.y),
        vx: Math.round(sim.vx),
        vy: Math.round(sim.vy),
        tumble: +sim.tumble.toFixed(2),
        tumbleV: +sim.tumbleV.toFixed(2),
        camY: Math.round(sim.camY),
        grounded: sim.grounded,
        world: `${sim.world.W}x${sim.world.H}`,
        z: sim.world.Z,
        jet: sim.jetOn,
        weapon: sim.def.id,
        nades: sim.nades.length,
        rockets: sim.rockets.length,
      };
    },
    sig: () => {
      if (!sim) return null;
      let empty = 0;
      let debris = 0;
      let solid = 0;
      const mat = sim.world.mat;
      for (let i = 0; i < mat.length; i++) {
        if (mat[i] === 0) empty++;
        else if (mat[i] === 3) debris++;
        else if (mat[i] === 2) solid++;
      }
      return { empty, debris, solid, removed: sim.world.removedCells, fire: sim.fire.cells.size, letters: sim.letters.bodies.length, popped: sim.letters.popped, chunks: sim.chunks.list.length };
    },
    skin: () => readHero(),
    blocks: () => sim?.blockCensus() ?? null,
    skyTime,
    skyCost,
    skyStar,
    skyComet,
    skyBlast,
    skyBreak,
    skyGlitch,
  };
  requestAnimationFrame(frame);
  bootUrl();
}

void boot();
